import React, { useState, useRef, useEffect } from 'react';
import {
  owesDisposition,
  isDeployedOnLiveJob,
  dispositionMessage,
  isOnFinishedJob,
} from '../lib/equipmentDisposition';
import DatePicker from '../components/DatePicker';
import {
  Wrench, Plus, X, CheckCircle, AlertTriangle, Boxes,
  MapPin, Shield, XCircle, Truck, Pencil, Trash2, ArrowRightLeft, Forward,
  Upload, Loader2, FileText, ExternalLink, ChevronRight, Save,
  ChevronDown, ClipboardCheck, Gauge, Radiation,
} from 'lucide-react';
import SortTh, { sortCompare, makeToggleSort } from '../components/SortTh';
import { useCollapsibleGroups } from '../hooks/useCollapsibleGroups';
import ConfirmModal from '../components/ui/ConfirmModal';
import { calendarDayString, daysUntilCalendarDate, formatCalendarDate, instantTenantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlState';
import { useAuth } from '../contexts/AuthContext';
import { canReadPreuse } from '../lib/preuse';
import { canReadPc } from '../lib/pressure-control';
import { canReadRad, RAD_LIST } from '../lib/radiation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiClient, openUploadDownload } from '../api/client';
import { putToPresignedUrl } from '../api/upload';
import { toast } from 'sonner';
import type { AssetKind } from '../types/equipment';
import { EQUIPMENT_STATUS_LABELS } from '../lib/equipment-status';
import { CERT_LEVEL_PRESETS, certLevelLabel, isPresetDayLimit } from '../lib/equipmentCertLevel';
import { useLocale } from '../contexts/LocaleContext';
import { CertDayCounter } from '../components/CertDayCounter';
import { Switch } from '../components/ui/switch';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { isTransferDestination } from '../lib/transfer-destination';
import { canReturnToYard } from '../lib/return-to-yard';
import { canManageEquipmentCertDocuments, canWriteCertFields } from '../lib/roles';
import { buildEquipmentTypeOptions } from '../lib/equipmentTypeOptions';
import { toEquipmentType, compareEquipmentTypes } from '../types/equipment-type';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import type { EquipmentType as CatalogEquipmentType } from '../types/equipment-type';
import { describeLastJob, parseLastJob } from '../lib/equipment-history';
import { Tooltip } from '../components/ui/tooltip';

// --- Types (matches backend DB schema) ---
interface EquipmentType {
  id: string;
  name: string;
  isStandard: boolean;
  isActive: boolean;
}

interface Equipment {
  id: string;
  name: string;
  type: string;
  equipmentTypeId?: string;
  /** Joined from equipment_types.name by GET /equipment. The canonical name;
   *  `type` beside it is the legacy free-text column. Used only as a grouping
   *  fallback — the two are NOT reconciled here. */
  equipmentTypeName?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  size?: string;
  capacity?: string;
  status: 'available' | 'reserved' | 'in_use' | 'left_on_site' | 'maintenance' | 'retired';
  current_job_id?: string | null;
  currentJobId?: string | null;
  reserved_date?: string | null;
  reservedDate?: string | null;
  reserved_job_number?: string | null;
  reservedJobNumber?: string | null;
  currentYardId?: string;
  homeYardId?: string;
  lastKnownRig?: string;
  lastKnownWell?: string;
  lastInspectionDate?: string;
  nextInspectionDate?: string;
  certDayLimit?: number;
  certStartDate?: string;
  certExpiryDate?: string;
  certDescription?: string;
  workingDaysSinceInspection?: number | null;
  linkedJobStatus?: string | null;
  linked_job_status?: string | null;
  linkedJobWell?: string | null;
  linked_job_well?: string | null;
  assetKind?: AssetKind;
  asset_kind?: AssetKind;
  licensePlate?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  notes?: string;
  createdAt?: string;
}

interface ServiceLocation {
  id: string;
  name: string;
  city: string;
}

// --- Status Config ---
const STATUS_CFG: Record<string, { label: string; dot: string; text: string }> = {
  available:     { label: EQUIPMENT_STATUS_LABELS.available, dot: 'bg-green-400',  text: 'text-green-400' },
  reserved:      { label: EQUIPMENT_STATUS_LABELS.reserved, dot: 'bg-purple-400', text: 'text-purple-400' },
  in_use:        { label: EQUIPMENT_STATUS_LABELS.in_use, dot: 'bg-blue-400',   text: 'text-blue-400' },
  left_on_site:  { label: EQUIPMENT_STATUS_LABELS.left_on_site, dot: 'bg-amber-400',  text: 'text-amber-400' },
  maintenance:   { label: EQUIPMENT_STATUS_LABELS.maintenance, dot: 'bg-red-400',    text: 'text-red-400' },
  retired:       { label: EQUIPMENT_STATUS_LABELS.retired, dot: 'bg-gray-400',   text: 'text-gray-400' },
};

/**
 * The register's status pill and row rail — the Quotes / Rate Sheets badge,
 * dark ink on a saturated signal fill. EXHAUSTIVE over `Equipment['status']`
 * (the six values the row can display; `reserved` is also what an `available`
 * row with a current job displays as, see `displayStatus` below).
 *
 * SIX STATUSES, FIVE SIGNAL HUES. The `--fi-signal-*` palette has no purple, so
 * Reserved and In Use share the blue fill: both are "committed to a job", and
 * the pill's label is what tells them apart. Retired is a dead row and takes
 * the quiet steel edge, as an inactive rate sheet does.
 *
 * A value outside the enum reads `—` on slate with a transparent rail — never
 * a guessed label. This used to fall back to Available's config, which is a
 * confident wrong answer about a row the screen could not classify.
 */
const STATUS_BADGE: Record<Equipment['status'], { label: string; fill: string; rail: string }> = {
  available:    { label: EQUIPMENT_STATUS_LABELS.available, fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  reserved:     { label: EQUIPMENT_STATUS_LABELS.reserved, fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue' },
  in_use:       { label: EQUIPMENT_STATUS_LABELS.in_use, fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue' },
  left_on_site: { label: EQUIPMENT_STATUS_LABELS.left_on_site, fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  maintenance:  { label: EQUIPMENT_STATUS_LABELS.maintenance, fill: 'bg-fi-signal-red',   rail: 'border-l-fi-red' },
  retired:      { label: EQUIPMENT_STATUS_LABELS.retired, fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

/**
 * The toast after a bulk delete. The counts live in the envelope's `data`; the
 * old toast read them off the envelope itself, found none, and said "Done" for
 * every outcome — including a batch that archived everything and deleted
 * nothing. A refusal (409 equipment_in_use, naming the units and their NDT or
 * Pipeline & Facility jobs) arrives through onError, not here.
 */
export function bulkDeleteSummary(body: any): string {
  const data = body && typeof body === 'object' && 'data' in body ? body.data : body;
  const parts: string[] = [];
  if (data?.deletedCount) parts.push(`Removed ${data.deletedCount}`);
  if (data?.archivedCount) parts.push(`archived ${data.archivedCount} (still referenced by other records)`);
  if (data?.skippedCount) parts.push(`skipped ${data.skippedCount}`);
  return parts.join(', ') || 'Nothing was removed';
}

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

const statusSkin = (status: string | null | undefined) =>
  (status && STATUS_BADGE[status as Equipment['status']]) || UNKNOWN_STATUS;

function EquipmentStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = statusSkin(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** Type is a category, not a state, so it takes the neutral outlined pill. */
const PILL =
  'inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text';

/** One shape for every per-row action button on the register. */
const ROW_ACTION =
  'flex h-8 items-center gap-1 whitespace-nowrap rounded-fi-control border border-black/20 px-2.5 text-xs font-bold text-fi-signal-ink transition disabled:opacity-50';
const ROW_ICON =
  'rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-white';


/** nextInspectionDate is an INSTANT (api#838): counted from the tenant's day it
 *  falls on, not its UTC prefix, which is a day late after 18:00 MDT. */
function getInspectionStatus(eq: Equipment, timeZone: string | null): { label: string; color: string; icon: any } | null {
  if (!eq.nextInspectionDate) return null;
  const daysLeft = daysUntilCalendarDate(instantTenantDay(eq.nextInspectionDate, timeZone), timeZone);
  if (daysLeft == null) return null;
  if (daysLeft < 0) return { label: 'Inspection OVERDUE!', color: 'text-red-400', icon: XCircle };
  if (daysLeft <= 30) return { label: `Inspection due in ${daysLeft} days`, color: 'text-amber-400', icon: AlertTriangle };
  return { label: 'Inspection: Current', color: 'text-green-400', icon: CheckCircle };
}

/** Resolve equipment location to a human-readable string */
function getLocationLabel(eq: Equipment, yardMap: Record<string, ServiceLocation>): string | null {
  if (eq.status === 'in_use' || eq.status === 'left_on_site') {
    // Prefer the live linked-job rig over the stale lastKnownRig snapshot
    const jobWell = eq.linked_job_well || eq.linkedJobWell;
    if (jobWell) return `at Rig ${jobWell}`;
    if (eq.lastKnownRig) return `at Rig ${eq.lastKnownRig}`;
    // Fall through to check yard
  }
  const yard = eq.currentYardId ? yardMap[eq.currentYardId] : eq.homeYardId ? yardMap[eq.homeYardId] : null;
  return yard ? `at ${yard.name}` : null;
}

// `finished_job` sits immediately after `in_use` because it is a SUBSET of it —
// the same gear, narrowed to the rows where the job has ended. Putting it at the
// end would read as an unrelated seventh category.
const FILTERS = ['all', 'available', 'reserved', 'in_use', 'finished_job', 'left_on_site', 'maintenance', 'cert_tracking'] as const;
const FILTER_LABELS: Record<string, string> = {
  all: 'All', available: 'Available', reserved: 'Reserved', in_use: 'In Use',
  // Not "Finished Job" — the label has to say what is WRONG, not what is true.
  // A dispatcher scanning chips needs to know this one is a to-do list.
  finished_job: 'Job Finished',
  left_on_site: 'On Location', maintenance: 'Maintenance', cert_tracking: 'Cert Tracking',
};

/**
 * Whether an asset belongs under a status chip. ONE predicate, read by the
 * filter and by every chip's count, so a chip cannot claim a number its own
 * list does not show. The branches are the filter chain this screen always
 * had, moved here unchanged.
 */
function matchesStatusFilter(e: Equipment, f: string): boolean {
  switch (f) {
    case 'all': return true;
    case 'cert_tracking': return !!e.certDayLimit;
    case 'reserved': return e.status === 'available' && !!e.currentJobId;
    case 'available': return e.status === 'available' && !e.currentJobId;
    // `finished_job` is not an `equipment.status` value — it is a relationship
    // between the asset and its job — so it cannot fall through to the
    // equality branch below.
    case 'finished_job': return isOnFinishedJob(e);
    default: return e.status === f;
  }
}

/** Bucket for rows carrying neither a legacy `type` nor a joined type name. */
const UNCATEGORIZED = 'Uncategorized';

/**
 * The one name a piece of equipment is filed under — used by the filter, the
 * group headers and the filter dropdown, so all three cannot disagree.
 *
 * `equipmentTypeName` FIRST. It is the catalog name, joined through
 * equipment_type_id, and it is authoritative; `type` is a cache the database
 * maintains from it via the 0334 triggers.
 *
 * THE DRIFT THAT MOTIVATED THIS IS REPAIRED, AND THIS DOCBLOCK USED TO SAY
 * OTHERWISE. It read "the two drift apart on 60 live rows" with no date, which
 * is a claim about a live system stated as a standing property. It was true
 * when written and is not true now, and it was cited a fortnight later as
 * present-tense justification for opening an API ticket that turned out to fix
 * nothing (api#623, closed).
 *
 * The provenance, so nobody has to reconstruct it again: fieldiq-api#384
 * measured 60 divergent rows on production 2026-08-23 — PAR 32 ("Elevator"
 * against a catalog type named "Elevators"), Smith 18, PAR 8, Dutch 2 —
 * created by 0246, which matched on a normalized name, set the FK and left
 * `e.type` alone. 0334 then made the FK decide the text on every write, rename
 * included, and 0335 backfilled the remainder.
 *
 * RE-MEASURED ACROSS ALL SIX PRODUCTION TENANTS 2026-09-09: ZERO divergent
 * rows. Dutch, Lone Star, Smith, PAR, Dash and TNT — 719 assets, none whose
 * cached name disagrees with its catalog type. So preferring the join is now
 * defence in depth rather than a live workaround. KEEP IT ANYWAY: PATCH
 * /equipment/:id pipes a raw body through sanitizeUpdate(), so `type` stays
 * independently writable by any client and the triggers are what close that,
 * not this screen. But do not cite drift as a present-tense fact — measure it.
 *
 * `type` remains the fallback because 15 rows deliberately carry no type id,
 * and their free text is the only description they have. Those are Dutch 7 and
 * Lone Star 8 as of 2026-09-09, the same 15 api#384 left alone for the tenant
 * to decide. They are the rows with nothing for either screen to prefer, and
 * they are why this reads as a fallback chain rather than a single column.
 */
function typeKeyOf(eq: { type?: string | null; equipmentTypeName?: string | null }): string {
  return eq.equipmentTypeName || eq.type || UNCATEGORIZED;
}

const inputClass = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-sm text-fieldiq-text mb-1.5 font-medium';

interface ParsedItem {
  name: string;
  type: string;
  serialNumber: string | null;
  manufacturer: string | null;
  size: string | null;
  capacity: string | null;
  location: string | null;
  homeYardId: string | null;
  certDayLimit: number | null;
  certDescription: string | null;
  notes: string | null;
}

type ImportState = 'idle' | 'uploading' | 'preview' | 'saving';

// ==========================================
// TRANSFER TO JOB
// ==========================================

/**
 * The office half of a lease-to-lease equipment handoff.
 *
 * `POST /equipment/:id/transfer-to-job` has existed since #444/#475 and was
 * reachable from exactly ONE place in this app: the field screen's
 * FieldEquipmentScreen. So a dispatcher looking at an On Location asset had a
 * single action available — Return to Yard — and no way to hand it to the crew
 * already heading for that lease.
 *
 * What the office did instead, measured on production 2026-08-31: nothing, or
 * a workaround. TNT's J-4303 left two assets at Ensign 418 and the only person
 * who can see them is the one hand crewed on that finished job; the five hands
 * on the next job at the same rig cannot. The office lever for that today is to
 * add somebody to the completed job's crew, which is a lie about who worked it.
 *
 * NOT THE SAME THING AS THE `ArrowRightLeft` BUTTON BESIDE IT, and the two are
 * one click apart, so the distinction is worth stating:
 *
 *   POST /equipment/:id/transfer         — a LOCATION edit. Writes
 *                                          last_known_rig / current_yard_id and
 *                                          leaves current_job_id and every
 *                                          job_equipment_assignments row alone.
 *   POST /equipment/:id/transfer-to-job  — a JOB handoff. Closes the outgoing
 *                                          assignment (carrying the old rig and
 *                                          well onto it), opens a new one with
 *                                          assigned_source 'transfer', and
 *                                          repoints current_job_id.
 *
 * Only the second one puts the asset on another crew's My Equipment, which is
 * the entire request. The first is titled "Move to shop or rig" for that reason
 * — it had no label at all until this button gave it something to be confused
 * with.
 *
 * The reason field is optional, matching the field modal and the API. The
 * destination job is already most of the answer to "why did it move", and a
 * required field on a control used several times a shift fills with one
 * character.
 */
function JobTransferModal({
  equipment,
  pending,
  onClose,
  onConfirm,
}: {
  equipment: Equipment;
  pending: boolean;
  onClose: () => void;
  onConfirm: (jobId: string, jobLabel: string, reason: string) => void;
}) {
  // Fetched here rather than on the screen so the list is only requested when a
  // dispatcher actually opens the picker. Shares the ['jobs'] cache key with
  // every other consumer of GET /jobs.
  const { data, isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.jobs.list().then(r => unwrapArray<any>(r.data)),
  });

  const currentJobId = equipment.currentJobId ?? equipment.current_job_id ?? null;
  // isTransferDestination mirrors the API's own destination rule; see the
  // helper. Excluding the job the asset is already on matches the API too — it
  // 400s that case rather than writing a no-op pair of assignment rows.
  const jobs: any[] = (Array.isArray(data) ? data : [])
    .filter(isTransferDestination)
    .filter((j: any) => j.id !== currentJobId);

  const [jobId, setJobId] = useState('');
  const [reason, setReason] = useState('');
  const selected = jobs.find(j => j.id === jobId);

  const labelFor = (j: any) => {
    const number = j.job_number || j.jobNumber || 'Job';
    const rig = j.rig_name || j.rigName;
    return rig ? `${number} — ${rig}` : number;
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-1">Transfer to Job</h3>
        <p className="text-sm text-fieldiq-text mb-4">
          Move <span className="text-white font-medium">{equipment.serialNumber || equipment.name}</span> to another job without bringing it back to a yard.
        </p>
        <div className="mb-4">
          <label className={labelClass} htmlFor="equipment-transfer-job">Destination Job</label>
          <select
            id="equipment-transfer-job"
            className={inputClass}
            value={jobId}
            onChange={e => setJobId(e.target.value)}
          >
            <option value="">{isLoading ? 'Loading jobs...' : 'Select job...'}</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>{labelFor(j)}</option>
            ))}
          </select>
          {!isLoading && jobs.length === 0 && (
            <p className="text-amber-400 text-xs mt-2">No open jobs to transfer to.</p>
          )}
        </div>
        <div className="mb-5">
          <label className={labelClass} htmlFor="equipment-transfer-reason">Reason</label>
          <textarea
            id="equipment-transfer-reason"
            rows={2}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Optional — why is it moving?"
            className={`${inputClass} resize-none`}
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium">Cancel</button>
          <button
            onClick={() => selected && onConfirm(selected.id, labelFor(selected), reason.trim())}
            disabled={!selected || pending}
            className="flex-1 bg-blue-500 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-400 transition-colors"
          >
            {pending ? 'Transferring...' : 'Confirm Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MAIN SCREEN
// ==========================================
export default function EquipmentScreen() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Chip, sort and the two filter selects live in the URL (useUrlState), so
  // Back from a unit returns to the same view. `status` was the inbound key.
  const [filter, setFilter] = useUrlState<string>('status', 'all');
  const [sortBy, setSortBy] = useUrlState<string>('sort', 'name');
  const [sortDir, setSortDir] = useUrlState<'asc' | 'desc'>('dir', 'asc');
  const [typeFilter, setTypeFilter] = useUrlState<string>('type', '');
  const [yardFilter, setYardFilter] = useUrlState<string>('yard', '');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Equipment | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [transferItem, setTransferItem] = useState<Equipment | null>(null);
  const [transferYardId, setTransferYardId] = useState('');
  const [transferType, setTransferType] = useState<'shop' | 'rig'>('shop');
  const [transferRigName, setTransferRigName] = useState('');
  const [returnItem, setReturnItem] = useState<Equipment | null>(null);
  const [jobTransferItem, setJobTransferItem] = useState<Equipment | null>(null);
  const [returnYardId, setReturnYardId] = useState('');

  // Type-group collapse state. Closed by default: TNT runs 200+ assets across
  // a dozen types and wanted to pick a TYPE without scrolling past every unit
  // in the ones above it. A newly-appearing group inherits the current default,
  // so a group the API returns later does not silently undo an Expand all.
  const { toggleGroup, isCollapsed, expandAll, collapseAll } = useCollapsibleGroups({ defaultCollapsed: true });

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkStatusMenu, setBulkStatusMenu] = useState(false);
  const [bulkLocationMenu, setBulkLocationMenu] = useState(false);
  const [bulkReturnMenu, setBulkReturnMenu] = useState(false);

  // Import state
  const [importState, setImportState] = useState<ImportState>('idle');
  const [importItems, setImportItems] = useState<ParsedItem[]>([]);
  const [importHomeYardId, setImportHomeYardId] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  const { data: equipmentData, isLoading } = useQuery({
    queryKey: ['equipment'],
    queryFn: () => api.equipment.list().then(r => r.data.data ?? r.data ?? []),
  });
  const equipment: Equipment[] = equipmentData ?? [];

  const { data: equipmentTypesData } = useQuery<EquipmentType[]>({
    queryKey: ['equipment-types'],
    queryFn: () => api.equipmentTypes.list().then(r => r.data.data ?? r.data ?? []),
  });
  const equipmentTypes: EquipmentType[] = (equipmentTypesData ?? [])
    .filter(t => t.isActive !== false)
    .sort((a, b) => a.isStandard === b.isStandard ? a.name.localeCompare(b.name) : a.isStandard ? -1 : 1);

  const { data: certSummaryData } = useQuery({
    queryKey: ['equipment-cert-summary'],
    queryFn: () => api.equipment.certSummary().then(r => r.data.data ?? r.data ?? []),
  });
  const certSummaryMap: Record<string, any> = {};
  (certSummaryData ?? []).forEach((item: any) => { certSummaryMap[item.id] = item; });

  const { data: yardsData } = useQuery({
    queryKey: ['service-locations'],
    queryFn: () => api.serviceLocations.list().then(r => r.data.data ?? r.data ?? []),
  });
  const yards: ServiceLocation[] = yardsData ?? [];
  const yardMap: Record<string, ServiceLocation> = {};
  yards.forEach(y => { yardMap[y.id] = y; });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['equipment'] });
    queryClient.invalidateQueries({ queryKey: ['equipment-cert-summary'] });
    queryClient.invalidateQueries({ queryKey: ['available-equipment'] });
  };

  /*
   * ─── Field-crew equipment capability ────────────────────────────────────
   *
   * `companies.field_crew_equipment_enabled`. Until now the ONLY way to turn it
   * on was raw SQL, so an operator could not enable their own feature.
   *
   * The flag has its OWN endpoint — GET/PATCH /settings/field-crew-equipment —
   * carrying this one boolean and nothing else. It used to ride on the
   * /settings/field-tickets section, which was an artifact of that being an
   * existing admin-gated route rather than a fit: the Field Tickets settings
   * page never read or wrote this flag. fieldiq-api #397 moved it so its gate
   * could differ from that section's without also handing a dispatcher
   * `next_ticket_number` — a live counter — and the pay-visibility toggles.
   *
   * SEPARATE QUERY KEY from pages/settings/FieldTicketsPage, which is correct
   * now rather than a regression: they are different endpoints returning
   * different rows, and the flag is no longer in the section's payload at all.
   *
   * This is NOT a third reader of the column. The field screen reads the same
   * value from GET /employee/my-jobs/:id (FieldJobDetail), and an API test
   * asserts the two agree. One column, two readers, and this is the write side
   * of reader #1.
   *
   * WHO MAY FLIP IT: admin and dispatcher, mirroring the route. The query is
   * gated on the same list so the three roles who cannot do not fire a request
   * for a control they will never see. Dispatch already assigns equipment —
   * PATCH /dispatch/jobs/:id/equipment is theirs — so whether crews may assign
   * their own is a dispatch decision.
   *
   * A LITERAL LIST, and deliberately no elevation lookup — there is none on this
   * control to delete. `dispatcher_admin_access` is not on /auth/me, so it cannot
   * be evaluated here. Same call as canUploadSourceForm in lib/roles.ts:
   * under-granting an elevated office_staff costs a toggle, whereas guessing at
   * elevation shows someone a switch that 403s.
   *
   * ⚠️ The OTHER `isAdmin` in this file (the equipment-TYPE control inside the
   * edit modal, ~1300 lines down) is a different endpoint and is deliberately
   * NOT widened. Do not fold the two together.
   */
  const canToggleCrewEquipment =
    user?.role === 'admin' || user?.role === 'dispatcher';

  const { data: crewEquipmentSetting, isLoading: ftSettingsLoading } = useQuery({
    queryKey: ['settings', 'field-crew-equipment'],
    queryFn: () =>
      apiClient.get('/settings/field-crew-equipment')
        .then(r => unwrap<Record<string, any>>(r.data) ?? {}),
    enabled: canToggleCrewEquipment,
  });

  const crewEquipmentEnabled = crewEquipmentSetting?.field_crew_equipment_enabled === true;

  const crewEquipmentMutation = useMutation({
    /*
     * Exactly this one key, and now the endpoint carries only this one key —
     * its `.strict()` schema takes `field_crew_equipment_enabled` and nothing
     * else, so there is no longer a live `next_ticket_number` counter on the
     * other side of this call to walk backwards. Sending one key remains
     * correct; it is no longer the only thing standing between this switch and
     * the ticket numbering.
     */
    mutationFn: (next: boolean) =>
      apiClient.patch('/settings/field-crew-equipment', { field_crew_equipment_enabled: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'field-crew-equipment'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error || 'Could not change that setting'),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.equipment.create(data),
    onSuccess: invalidateAll,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.equipment.update(id, data),
    onSuccess: invalidateAll,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.equipment.delete(id),
    onSuccess: invalidateAll,
  });

  const transferMutation = useMutation({
    mutationFn: ({ id, toYardId, toRig }: { id: string; toYardId?: string; toRig?: string }) =>
      apiClient.post(`/equipment/${id}/transfer`, { toYardId: toYardId || undefined, toRig: toRig || undefined }),
    onSuccess: () => {
      invalidateAll();
      setTransferItem(null);
      setTransferYardId('');
    },
  });

  /**
   * The office half of POST /equipment/:id/transfer-to-job.
   *
   * DELIBERATELY NOT `transferMutation` ABOVE. That one posts to
   * /equipment/:id/transfer, a location edit that never touches
   * job_equipment_assignments. Two endpoints, one word; the names are the only
   * thing keeping them apart in this file.
   *
   * onError is present and specific. A transfer is refused for reasons a
   * dispatcher can act on -- the destination job is not scheduled or active,
   * the asset is not deployed, the job is already the one it is on -- and the
   * API says which in `error`. Collapsing that into a generic string, or into
   * the success toast, is the shape frontend #421 shipped and #452 had to undo:
   * a refusal rendered under a green checkmark.
   */
  const transferToJobMutation = useMutation({
    mutationFn: ({ id, destinationJobId, reason }: { id: string; destinationJobId: string; jobLabel: string; reason?: string }) =>
      api.equipment.transferToJob(id, { destinationJobId, reason: reason || undefined }),
    onSuccess: (_data, vars) => {
      invalidateAll();
      setJobTransferItem(null);
      toast.success(`Equipment transferred to ${vars.jobLabel}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to transfer equipment');
    },
  });

  const returnToYardMutation = useMutation({
    mutationFn: ({ id, destinationYardId }: { id: string; destinationYardId: string }) =>
      api.equipment.returnFromSite(id, { destinationYardId }),
    onSuccess: () => {
      invalidateAll();
      setReturnItem(null);
      toast.success('Equipment returned to yard');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to return equipment');
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.equipment.bulkDelete(ids).then(r => r.data),
    onSuccess: (body) => {
      invalidateAll();
      setSelectedIds(new Set());
      toast.success(bulkDeleteSummary(body));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Bulk delete failed');
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: string }) =>
      api.equipment.bulkStatus(ids, status).then(r => r.data),
    onSuccess: () => {
      invalidateAll();
      setSelectedIds(new Set());
      toast.success('Status updated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Bulk status update failed');
    },
  });

  // Bulk Return to Yard. NOT bulkStatus(ids, 'available'), which is the button
  // an office user reached for before and which closes the assignment rows while
  // DISCARDING the destination — the asset ends up "back somewhere". This one
  // records the yard on every asset in one call.
  const bulkReturnMutation = useMutation({
    mutationFn: ({ ids, yardId }: { ids: string[]; yardId: string }) =>
      api.equipment.bulkReturn(ids, yardId).then(r => r.data),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      setSelectedIds(new Set());
      const d = res?.data ?? res;
      const returned = d?.returnedCount ?? 0;
      const skipped = d?.skippedCount ?? 0;
      // `returned === 0 && skipped > 0` must never render as a bare success —
      // that is the bulk-import rule, and the reason the skip count is spoken
      // rather than swallowed. A returned-nothing result is a WARNING toast, not
      // a green tick: a refusal styled as a success is how #421 turned "you
      // don't have permission" into a checkmark.
      if (returned === 0) {
        toast.error(
          skipped > 0
            ? `Nothing returned — ${skipped} item${skipped === 1 ? ' was' : 's were'} not out on a job.`
            : 'Nothing returned.',
        );
        return;
      }
      toast.success(
        skipped > 0
          ? `${returned} returned to yard · ${skipped} skipped (not out on a job)`
          : `${returned} returned to yard`,
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Bulk return failed');
    },
  });

  const bulkLocationMutation = useMutation({
    mutationFn: ({ ids, location }: { ids: string[]; location: string }) =>
      api.equipment.bulkLocation(ids, location).then(r => r.data),
    onSuccess: () => {
      invalidateAll();
      setSelectedIds(new Set());
      toast.success('Location updated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Bulk location update failed');
    },
  });

  // Clear selection when filter or type changes
  useEffect(() => { setSelectedIds(new Set()); }, [filter, typeFilter, yardFilter]);

  function matchLocationToYard(location: string | null | undefined): string | null {
    if (!location || yards.length === 0) return null;
    const loc = location.toLowerCase().trim();
    for (const y of yards) {
      if (y.city.toLowerCase() === loc || y.name.toLowerCase() === loc) return y.id;
    }
    for (const y of yards) {
      if (loc.includes(y.city.toLowerCase()) || y.city.toLowerCase().includes(loc)) return y.id;
      if (loc.includes(y.name.toLowerCase()) || y.name.toLowerCase().includes(loc)) return y.id;
    }
    return null;
  }

  const importMutation = useMutation({
    mutationFn: (file: File) => api.equipment.importFile(file),
    onSuccess: (res) => {
      const parsed = res.data.data;
      const items = (parsed.items || []).map((item: ParsedItem) => ({
        ...item,
        homeYardId: matchLocationToYard(item.location) || null,
      }));
      setImportItems(items);
      setImportError(null);
      setImportState('preview');
    },
    onError: (err: any) => {
      const status = err.response?.status || 'no response';
      const url = err.config?.baseURL + err.config?.url;
      const serverMsg = err.response?.data?.error;
      const msg = `${serverMsg || err.message || 'Failed to parse file'} [${status} ${url}]`;
      console.error('Equipment import error:', { status, url, serverMsg, fullError: err });
      setImportError(msg);
      setImportState('idle');
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const confirmImportMutation = useMutation({
    mutationFn: (data: any) => api.equipment.confirmImport(data),
    onSuccess: () => {
      invalidateAll();
      resetImport();
    },
    onError: (err: any) => {
      setImportError(err.response?.data?.error || 'Failed to save equipment');
      setImportState('preview');
    },
  });

  function resetImport() {
    setImportState('idle');
    setImportItems([]);
    setImportHomeYardId('');
    setImportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so re-selecting the same file fires onChange again
    if (fileInputRef.current) fileInputRef.current.value = '';
    setImportState('uploading');
    setImportError(null);
    importMutation.mutate(file);
  }

  function handleConfirmImport() {
    setImportState('saving');
    confirmImportMutation.mutate({
      items: importItems,
      homeYardId: importHomeYardId || null,
    });
  }

  function updateImportItem(index: number, field: keyof ParsedItem, value: any) {
    setImportItems(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  }

  function removeImportItem(index: number) {
    setImportItems(prev => prev.filter((_, i) => i !== index));
  }

  const statusFiltered = equipment.filter(e => matchesStatusFilter(e, filter));

  const typeFiltered = typeFilter
    ? statusFiltered.filter(e => typeKeyOf(e) === typeFilter)
    : statusFiltered;

  const yardFiltered = yardFilter
    ? typeFiltered.filter(e => {
        const effectiveYard = e.currentYardId || e.homeYardId;
        return yardFilter === '_none' ? !effectiveYard : effectiveYard === yardFilter;
      })
    : typeFiltered;

  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  // The selected rows themselves, not just their ids. The bulk status menu
  // needs each asset's own state to decide whether the transition owes a
  // disposition — a count of ids cannot answer that.
  //
  // Read off `equipment` (the full list) rather than `filtered`, because the
  // selection survives a filter change: narrowing the view must not silently
  // drop a selected-but-hidden asset out of the check and let the menu offer a
  // transition the API will refuse for it.
  const selectedEquipment = equipment.filter((e: Equipment) => selectedIds.has(e.id));
  // The selected assets a return is actually available on. Read off the same
  // full list, for the same reason: narrowing the view must not silently
  // change what the bulk bar would do.
  const returnableSelected = selectedEquipment.filter((e: Equipment) => canReturnToYard(e.status));

  const filtered = [...yardFiltered].sort((a, b) => {
    let aVal: any, bVal: any;
    switch (sortBy) {
      case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
      case 'type': aVal = a.type || ''; bVal = b.type || ''; break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      case 'location': aVal = getLocationLabel(a, yardMap); bVal = getLocationLabel(b, yardMap); break;
      case 'inspection': {
        const ai = getInspectionStatus(a, timeZone);
        const bi = getInspectionStatus(b, timeZone);
        aVal = ai?.color === 'red' ? 0 : ai?.color === 'amber' ? 1 : 2;
        bVal = bi?.color === 'red' ? 0 : bi?.color === 'amber' ? 1 : 2;
        break;
      }
      default: aVal = a.name || ''; bVal = b.name || ''; break;
    }
    return sortCompare(aVal, bVal, sortDir);
  });

  // Group the already-filtered, already-sorted list. Partitioning preserves
  // relative order, so SortTh keeps sorting WITHIN a group rather than across
  // groups, and every filter above stays untouched. A group can only exist
  // because a row put it there, so a type with zero units never renders.
  const groupedEquipment = (() => {
    const typeOrder = new Map(equipmentTypes.map((t, i) => [t.name, i]));
    const groupMap = new Map<string, Equipment[]>();
    for (const eq of filtered) {
      const key = typeKeyOf(eq);
      const bucket = groupMap.get(key);
      if (bucket) bucket.push(eq);
      else groupMap.set(key, [eq]);
    }
    return Array.from(groupMap, ([type, items]) => ({ type, items })).sort((a, b) => {
      if (a.type === UNCATEGORIZED) return 1;
      if (b.type === UNCATEGORIZED) return -1;
      const ai = typeOrder.get(a.type) ?? Infinity;
      const bi = typeOrder.get(b.type) ?? Infinity;
      // Canonical equipment_types order first (standard, then alphabetical —
      // the same sort the dropdown uses); free-text names the table does not
      // carry fall to the end, alphabetically among themselves.
      if (ai !== bi) return ai - bi;
      return a.type.localeCompare(b.type);
    });
  })();

  // The rows a user can actually SEE right now: `filtered` minus everything
  // sitting inside a collapsed group.
  //
  // This exists because collapse is now the default. Select-all reads it, so a
  // bulk action can never reach an asset the operator never laid eyes on — see
  // the header checkbox for why that is the choice and not the alternative.
  const visibleEquipment = groupedEquipment
    .filter(({ type }) => !isCollapsed(type))
    .flatMap(({ items }) => items);

  const allGroupsCollapsed =
    groupedEquipment.length > 0 && groupedEquipment.every(({ type }) => isCollapsed(type));

  // Selected assets that are NOT on screen. Scoping select-all closes the
  // common path into this state but not the only one: select three elevators,
  // then collapse Elevators, and three selections are live and invisible. The
  // bulk bar says so rather than reporting a count the screen cannot account
  // for.
  const hiddenSelectedCount = selectedIds.size - visibleEquipment.filter(eq => selectedIds.has(eq.id)).length;

  // The dropdown must offer every key the data can produce, or a row is
  // unreachable through it.
  //
  // THIS IS THE BUG THAT HID 32 OF PAR'S 39 ELEVATORS. The options were built
  // from the CATALOG (`Elevators`) while the filter compared the FREE TEXT
  // (`Elevator`), so selecting "Elevators" returned the 7 rows whose text
  // happened to match and the other 32 were unreachable under every option —
  // "Elevator" was not in the list at all. Nothing errored.
  //
  // The API-side fix (0334/0335) makes `equipmentTypeName` authoritative for
  // every row that has a type, so the two now agree. The union is what keeps
  // the rows that DON'T have one reachable: 15 rows are deliberately staying
  // FK-null pending a tenant decision, and their free text is by definition not
  // a catalog name. Listing those keys is how somebody finds them to decide.
  const uniqueTypes = (() => {
    const canonical = equipmentTypes.map(t => t.name);
    const seen = new Set(canonical);
    const extra: string[] = [];
    for (const eq of equipment) {
      const key = typeKeyOf(eq);
      if (key === UNCATEGORIZED || seen.has(key)) continue;
      seen.add(key);
      extra.push(key);
    }
    extra.sort((a, b) => a.localeCompare(b));
    const hasUncategorised = equipment.some(eq => typeKeyOf(eq) === UNCATEGORIZED);
    return [...canonical, ...extra, ...(hasUncategorised ? [UNCATEGORIZED] : [])];
  })();

  /**
   * The band's figures and the chips' counts: tallies of the one list this
   * screen already loads, with no added query.
   *
   * WHY A TALLY IS HONEST HERE. GET /equipment returns every live asset for
   * the tenant — no LIMIT, no pagination (equipment.routes.ts, `WHERE
   * deleted_at IS NULL AND archived_at IS NULL`) — and no endpoint returns
   * per-status counts, so the loaded rows ARE the population. Each count runs
   * through `matchesStatusFilter`, the predicate the list itself uses.
   *
   * `undefined` until the list has arrived, and when it was refused: the
   * `?? []` above turns both into an empty array, and a tally of that would be
   * a confident zero. displayValue() renders undefined as a dash.
   *
   * JOB FINISHED is gear still `in_use` against a job that has ended. Measured
   * against production 2026-09-06 this was 17 for Dutch and 7 for PAR, sitting
   * behind no screen at all — `check:equipment-state` reports the estate clean,
   * correctly, because the model calls this a backlog rather than a
   * contradiction. Its chip count and band figure are the places it is visible.
   */
  const listLoaded = Array.isArray(equipmentData);
  const tallyOf = (f: string): number | undefined =>
    listLoaded ? equipment.filter(e => matchesStatusFilter(e, f)).length : undefined;

  const handleSave = async (
    item: Equipment,
    certCycleAction?: 'carry' | 'reset',
    certChangeReason?: string,
  ): Promise<string | null> => {
    const payload: any = {
      name: item.name,
      type: item.type,
      equipmentTypeId: item.equipmentTypeId || null,
      serialNumber: item.serialNumber || null,
      manufacturer: item.manufacturer || null,
      size: item.assetKind === 'vehicle' ? null : (item.size || null),
      capacity: item.assetKind === 'vehicle' ? null : (item.capacity || null),
      status: item.status,
      homeYardId: item.homeYardId || null,
      certDayLimit: item.assetKind === 'vehicle' ? null : (item.certDayLimit || null),
      certStartDate: item.assetKind === 'vehicle' ? null : (item.certStartDate || null),
      certExpiryDate: item.assetKind === 'vehicle' ? null : (item.certExpiryDate || null),
      certDescription: item.assetKind === 'vehicle' ? null : (item.certDescription || null),
      workingDaysSinceInspection: item.assetKind === 'vehicle' ? null : (item.workingDaysSinceInspection ?? null),
      // BOTH inspection-date columns are deliberately ABSENT, not null —
      // `lastInspectionDate` (#650) and `nextInspectionDate` (this change).
      // Nothing in this modal edits either, so sending them could only ever
      // echo back whatever the row held when the screen loaded, and a screen
      // opened before an inspection and saved after it writes the stale value
      // (usually NULL) over whatever was recorded in the gap. `sanitizeUpdate`
      // writes any key that is PRESENT, so absence is the only way to say "I am
      // not speaking about this column". The API owns both.
      asset_kind: item.assetKind || 'equipment',
      license_plate: item.assetKind === 'vehicle' ? (item.licensePlate || null) : null,
      vin: item.assetKind === 'vehicle' ? (item.vin || null) : null,
      odometer: item.assetKind === 'vehicle' && (item as any).odometer ? Number((item as any).odometer) : undefined,
      engine_hours: item.assetKind === 'vehicle' && (item as any).engineHours ? Number((item as any).engineHours) : undefined,
      // Present ONLY when the modal actually asked. Sending a default on every
      // save would tell the API a decision was made where none was, and would
      // silence the warning that tells us when the field can become required.
      ...(certCycleAction ? { certCycleAction } : {}),
      // Present ONLY when the count moved. Since API 0388 that write is a
      // `counter_set` ledger row and the server refuses it without a reason —
      // at the route and again with a CHECK constraint, because the row is
      // append-only and a blank explanation could never be corrected.
      ...(certChangeReason ? { certChangeReason } : {}),
    };
    try {
      if (editItem) {
        await updateMutation.mutateAsync({ id: item.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setShowModal(false);
      setEditItem(null);
      return null;
    } catch (err: any) {
      if (err?.response?.status === 409) {
        return err.response.data?.error || 'An item with this serial number already exists.';
      }
      toast.error(err?.response?.data?.error || 'Failed to save equipment');
      return null;
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeleteConfirm(null);
  };

  const handlePickedUp = (eq: Equipment) => {
    // A return now has to name its destination yard, so there is no yard-less
    // fast path any more. One yard means there is nothing to pick; zero yards
    // falls through to the picker, which shows the empty state and keeps
    // Confirm disabled rather than firing a request the API will reject.
    if (yards.length === 1) {
      returnToYardMutation.mutate({ id: eq.id, destinationYardId: yards[0].id });
      return;
    }
    setReturnItem(eq);
    setReturnYardId(eq.homeYardId || '');
  };

  /**
   * The register row opens its asset on Enter or Space — but ONLY when the row
   * itself has focus. These rows hold a checkbox and up to five action buttons,
   * so this is the fe#647 exception (CLAUDE.md, copied from Rate Sheets): no
   * button role on the row, because that would make every control inside it
   * presentational, and no handling of a key that bubbled up from one of them,
   * because Enter on Edit must open Edit rather than the detail page.
   */
  const openOnRowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return; // a key on an inner control is that control's
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/equipment/${id}`);
    }
  };

  const registerTitle = filter === 'all' ? 'All equipment' : FILTER_LABELS[filter] ?? 'Equipment';

  return (
    <div data-testid="equipment-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,.pdf,.docx"
        className="hidden"
        onChange={handleFileChange}
      />

      <PageHeaderBand
        eyebrow="Yard and field assets"
        title="Equipment"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate('/reports/equipment-maintenance')}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Wrench className="size-4" aria-hidden="true" />
              Maintenance Log
            </button>
            {/* Pre-use forms per equipment type (api#1209) — cross-vertical,
                shown to the roles the API lets read the register. */}
            {canReadPreuse(user?.role) && (
              <button
                type="button"
                onClick={() => navigate('/equipment/preuse-templates')}
                className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
              >
                <ClipboardCheck className="size-4" aria-hidden="true" />
                Pre-use Forms
              </button>
            )}
            {/* Pressure control — shared BOP certificates (api#1268). Cross-vertical,
                shown to the roles the API lets read the register. */}
            {canReadPc(user?.role) && (
              <button
                type="button"
                onClick={() => navigate('/equipment/pressure-control')}
                className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
              >
                <Gauge className="size-4" aria-hidden="true" />
                Pressure Control
              </button>
            )}
            {/* Radiation — the shared sealed-source module (api#1334, RAD-1). Like
                Pressure Control: no vertical gate, shown to the roles the API
                lets read it. */}
            {canReadRad(user?.role) && (
              <button
                type="button"
                onClick={() => navigate(RAD_LIST)}
                className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
              >
                <Radiation className="size-4" aria-hidden="true" />
                Radiation
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/import/equipment')}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Upload className="size-4" aria-hidden="true" />
              Import
            </button>
            <button
              type="button"
              onClick={() => { setEditItem(null); setShowModal(true); }}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Plus className="size-4" aria-hidden="true" />
              New
            </button>
          </>
        }
        stats={[
          {
            icon: <Boxes className="size-4" aria-hidden="true" />,
            label: 'Units',
            sub: 'Every asset listed',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(tallyOf('all')),
          },
          {
            icon: <MapPin className="size-4" aria-hidden="true" />,
            label: 'On Location',
            sub: 'Left on a lease',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(tallyOf('left_on_site')),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            // "Finished", not the chip's "Job Finished": this is the third
            // column, which the circle's edge crosses at 1024-1048px. The
            // longer label ran into it at 3.88:1; the circle was measured
            // against third-column labels no wider than Quotes' "Accepted".
            label: 'Finished',
            sub: 'Job has ended',
            tone: 'info',
            loading: isLoading,
            value: displayValue(tallyOf('finished_job')),
          },
          {
            icon: <Wrench className="size-4" aria-hidden="true" />,
            label: 'Maintenance',
            sub: 'Out of service',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(tallyOf('maintenance')),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/*
            * Field-crew equipment switch. Its own panel above the filters,
            * deliberately NOT in the band's action group, where the three
            * buttons are all navigation/create actions and a persistent
            * capability switch would read as a fourth button.
            *
            * Not rendered, rather than disabled, for roles the route refuses.
            * Everyone except field workers and customers reaches this page, so
            * office_staff, supervisor and logistics would meet a 403. A
            * greyed-out switch still says "this is nearly a thing you can do";
            * per PR #421 the refusal is withdrawn rather than styled.
            */}
          {canToggleCrewEquipment && (
            <div className="flex items-center justify-between gap-4 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
              <div className="flex-1 pr-6">
                <p className="text-sm font-extrabold text-white">
                  Let crews add their own equipment
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-fieldiq-muted">
                  Field workers can pick equipment for their assigned jobs from their phone.
                  Off by default — when off, only the office assigns equipment.
                </p>
              </div>
              {/*
                * `checked` is read straight from server state with no local mirror,
                * which is what makes a failed save safe: the switch cannot move until
                * a refetch confirms the write. An optimistic flip would leave it
                * looking ON after a 403, which is the one outcome this must not have.
                */}
              <Switch
                id="field-crew-equipment"
                aria-label="Let crews add their own equipment"
                checked={crewEquipmentEnabled}
                disabled={ftSettingsLoading || crewEquipmentMutation.isPending}
                onCheckedChange={(next) => crewEquipmentMutation.mutate(next)}
              />
            </div>
          )}

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
              <span className="text-sm font-extrabold text-white">{selectedIds.size} selected</span>
              {hiddenSelectedCount > 0 && (
                <span className="rounded-fi-pill border border-fi-amber/40 bg-fi-amber/10 px-2 py-0.5 text-xs text-fieldiq-text">
                  {hiddenSelectedCount} in a collapsed group
                </span>
              )}
              <div className="relative">
                <button
                  onClick={() => { setBulkStatusMenu(v => !v); setBulkLocationMenu(false); setBulkReturnMenu(false); }}
                  className="flex items-center gap-1.5 rounded-fi-pill border border-fieldiq-border bg-fieldiq-card px-4 py-1.5 text-sm font-bold text-fieldiq-text transition-colors hover:text-white"
                >
                  Change Status <ChevronDown size={14} />
                </button>
                {bulkStatusMenu && (
                  <>
                  <div className="fixed inset-0 z-20" onClick={() => setBulkStatusMenu(false)} />
                  <div className="absolute top-full left-0 mt-1 overflow-hidden bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl z-30 min-w-[160px]">
                    {Object.entries(STATUS_CFG).map(([key, cfg]) => {
                      // The SELECTED assets this status would owe a disposition
                      // for. A partial selection is the common case — a dozen
                      // tools picked, two of them still out — so the entry is
                      // disabled and says WHICH ones rather than failing on save
                      // for reasons the user cannot see from here.
                      const blocked = selectedEquipment.filter((e) => owesDisposition(e, key));
                      return (
                        <Tooltip key={key} content={blocked.length > 0 ? dispositionMessage(blocked) : undefined} className="w-full">
                          <button
                            disabled={blocked.length > 0}
                            onClick={() => {
                              if (blocked.length > 0) return;
                              setBulkStatusMenu(false);
                              bulkStatusMutation.mutate({ ids: Array.from(selectedIds), status: key });
                            }}
                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                              blocked.length > 0
                                ? 'text-fieldiq-muted cursor-not-allowed'
                                : 'text-fieldiq-text hover:bg-fieldiq-dark hover:text-white'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                            {cfg.label}
                            {blocked.length > 0 && (
                              <span className="ml-auto text-[11px]">
                                {blocked.length} still out
                              </span>
                            )}
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                  </>
                )}
              </div>
              {/* Bulk Return to Yard.
                  FIRST in the bar, ahead of Change Status, because it is the
                  action this bar was missing and the one that records where the
                  gear went. Change Status -> Available is one click away and
                  throws the destination away; putting the lossy option first is
                  how it stays the habit.

                  DISABLED, NOT REMOVED, when nothing selected is returnable — a
                  control that vanishes reads as a bug, and the title says which
                  of the selected assets is the problem. Eligibility is
                  `canReturnToYard`, the same helper the row button and the field
                  screen use, mirroring the API's two-status rule by name. */}
              <div className="relative">
                <Tooltip content={
                    returnableSelected.length === 0
                      ? 'None of the selected items are out on a job'
                      : returnableSelected.length < selectedIds.size
                        ? `${selectedIds.size - returnableSelected.length} of ${selectedIds.size} are not out on a job and will be skipped`
                        : undefined
                  }>
                  <button
                    onClick={() => { setBulkReturnMenu(v => !v); setBulkStatusMenu(false); setBulkLocationMenu(false); }}
                    disabled={returnableSelected.length === 0 || bulkReturnMutation.isPending}
                    className={`flex items-center gap-1.5 rounded-fi-pill px-4 py-1.5 text-sm font-bold transition-colors ${
                      returnableSelected.length === 0 || bulkReturnMutation.isPending
                        ? 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted cursor-not-allowed'
                        : 'border border-black/20 bg-fi-signal-green text-fi-signal-ink'
                    }`}
                  >
                    {bulkReturnMutation.isPending
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Truck size={14} />}
                    Return to Yard
                    {returnableSelected.length > 0 && returnableSelected.length < selectedIds.size && (
                      <span className="text-[11px] opacity-80">({returnableSelected.length})</span>
                    )}
                    <ChevronDown size={14} />
                  </button>
                </Tooltip>
                {bulkReturnMenu && (
                  <>
                  <div className="fixed inset-0 z-20" onClick={() => setBulkReturnMenu(false)} />
                  <div className="absolute top-full left-0 mt-1 bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl z-30 min-w-[220px] max-h-60 overflow-y-auto">
                    <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-fieldiq-muted border-b border-fieldiq-border">
                      Return {returnableSelected.length} to
                    </div>
                    {yards.length === 0 && (
                      <div className="px-4 py-3 text-sm text-fieldiq-muted">
                        No yards configured
                      </div>
                    )}
                    {yards.map(y => (
                      <button
                        key={y.id}
                        onClick={() => {
                          setBulkReturnMenu(false);
                          bulkReturnMutation.mutate({
                            // Only the returnable ones. Sending the rest would
                            // earn a skip apiece and a misleading skip count on a
                            // batch the user never meant to include them in.
                            ids: returnableSelected.map(e => e.id),
                            yardId: y.id,
                          });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-fieldiq-text hover:bg-fieldiq-dark hover:text-white transition-colors last:rounded-b-lg"
                      >
                        {y.name} — {y.city}
                      </button>
                    ))}
                  </div>
                  </>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => { setBulkLocationMenu(v => !v); setBulkStatusMenu(false); setBulkReturnMenu(false); }}
                  className="flex items-center gap-1.5 rounded-fi-pill border border-fieldiq-border bg-fieldiq-card px-4 py-1.5 text-sm font-bold text-fieldiq-text transition-colors hover:text-white"
                >
                  Reassign Location <ChevronDown size={14} />
                </button>
                {bulkLocationMenu && (
                  <>
                  <div className="fixed inset-0 z-20" onClick={() => setBulkLocationMenu(false)} />
                  <div className="absolute top-full left-0 mt-1 bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl z-30 min-w-[200px] max-h-60 overflow-y-auto">
                    {yards.map(y => (
                      <button
                        key={y.id}
                        onClick={() => {
                          setBulkLocationMenu(false);
                          bulkLocationMutation.mutate({ ids: Array.from(selectedIds), location: y.id });
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-fieldiq-text hover:bg-fieldiq-dark hover:text-white transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {y.name} — {y.city}
                      </button>
                    ))}
                  </div>
                  </>
                )}
              </div>
              <button
                onClick={() => { setBulkDeleteConfirm(true); setBulkStatusMenu(false); setBulkLocationMenu(false); setBulkReturnMenu(false); }}
                className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-4 py-1.5 text-sm font-bold text-fi-signal-ink transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="rounded-fi-pill border border-fieldiq-border bg-fieldiq-card px-4 py-1.5 text-sm font-bold text-fieldiq-text transition-colors hover:text-white"
              >
                Clear
              </button>
            </div>
          )}

          {/* Status chips. Every count is `tallyOf` — see there for why a
              tally of the loaded list is the whole count on this screen. No
              number is claimed while the list is in flight. */}
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {FILTERS.map(f => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {FILTER_LABELS[f]}
                  {!isLoading && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {displayValue(tallyOf(f))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              aria-label="Filter by type"
              className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber/50 focus:outline-none"
            >
              <option value="">All types</option>
              {uniqueTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {yards.length > 0 && (
              <select
                value={yardFilter}
                onChange={e => setYardFilter(e.target.value)}
                aria-label="Filter by shop"
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber/50 focus:outline-none"
              >
                <option value="">All shops</option>
                {yards.map(y => (
                  <option key={y.id} value={y.id}>{y.name}</option>
                ))}
                <option value="_none">No shop assigned</option>
              </select>
            )}
            {/* Required, not a convenience. This page has no free-text search,
                so with every group shut the ONLY way to reach an asset is the
                type dropdown — and that answers a different question than "show
                me everything". */}
            {groupedEquipment.length > 0 && (
              <button
                type="button"
                onClick={() => (allGroupsCollapsed ? expandAll() : collapseAll())}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm font-bold text-fieldiq-text transition-colors hover:text-white"
              >
                {allGroupsCollapsed ? 'Expand all' : 'Collapse all'}
              </button>
            )}
            <div className="flex gap-1 ml-auto md:hidden">
              {([['name', 'A–Z'], ['status', 'Status']] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => { setSortBy(key); setSortDir('asc'); }}
                  className={`rounded-fi-control px-3 py-1.5 text-xs font-bold transition-colors ${
                    sortBy === key ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Import error banner (shown when import fails before modal opens) */}
          {importError && importState === 'idle' && (
            <div className="flex items-center justify-between rounded-fi-panel border border-fi-red/40 bg-fi-red/10 p-3 text-sm text-fi-red">
              <span>{importError}</span>
              <button onClick={() => setImportError(null)} className="ml-3 text-fi-red hover:text-white">
                <X size={16} />
              </button>
            </div>
          )}

          {/* The register. The amber edge is on this panel; each row carries
              its status rail, never a second amber one. */}
          <section
            aria-labelledby="equipment-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Asset register</p>
                <h2 id="equipment-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">{registerTitle}</h2>
              </div>
            </header>

            {/* Loading state */}
            {isLoading && (
              <div className="px-4 py-12 text-center text-fieldiq-text">Loading equipment...</div>
            )}

            {!isLoading && filtered.length === 0 && (
              <div className="px-4 py-12 text-center text-fieldiq-text">No equipment matches this filter.</div>
            )}

            {/* Desktop Table */}
            {!isLoading && filtered.length > 0 && (
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[1240px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    {/* Transparent 4px so the columns line up with the railed rows. */}
                    <tr className="border-l-4 border-l-transparent">
                      <th className="w-10 px-4 py-3">
                        {/* Scoped to the rows on screen, not to `filtered`.
                            Groups are collapsed by default now, so reading
                            `filtered` here would arm Change Status / Delete over
                            assets the operator has never seen — on first load,
                            every asset the tenant owns behind a single click. It
                            cannot be made safe by a warning: the bulk menus decide
                            what a transition owes from the assets themselves, and
                            an operator cannot check a decision about rows that are
                            not rendered.

                            Disabled rather than a silent no-op when every group is
                            shut, because a checkbox that selects nothing reads as
                            broken rather than as empty. Expand all is next to the
                            type filter. */}
                        <input
                          type="checkbox"
                          aria-label="Select all visible equipment"
                          disabled={visibleEquipment.length === 0}
                          title={visibleEquipment.length === 0 ? 'Expand a type group to select rows' : undefined}
                          checked={visibleEquipment.length > 0 && visibleEquipment.every(eq => selectedIds.has(eq.id))}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedIds(new Set(visibleEquipment.map(eq => eq.id)));
                            } else {
                              setSelectedIds(new Set());
                            }
                          }}
                          className="w-4 h-4 rounded border-fieldiq-border text-fieldiq-accent focus:ring-fieldiq-accent bg-fieldiq-dark cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </th>
                      <SortTh label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Type" field="type" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                      <SortTh label="Location" field="location" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                      <th className="text-left text-xs uppercase tracking-wider font-bold text-fieldiq-muted px-4 py-3">Last Job</th>
                      <th className="text-left text-xs uppercase tracking-wider font-bold text-fieldiq-muted px-4 py-3">Cert</th>
                      <SortTh label="Cert Date" field="inspection" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                      <th className="text-left text-xs uppercase tracking-wider font-bold text-fieldiq-muted px-4 py-3 w-[120px]">Actions</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedEquipment.map(({ type, items }) => (
                      <React.Fragment key={type}>
                        {/* Group header — a full-width row, not a sticky overlay:
                            the table already scrolls inside <main>. It carries the
                            same transparent 4px as the heading row: a group is not
                            a status, so it claims no rail colour, but it keeps the
                            width so the columns below it do not shift. */}
                        <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/40">
                          <td colSpan={10} className="p-0">
                            <button
                              type="button"
                              onClick={() => toggleGroup(type)}
                              aria-expanded={!isCollapsed(type)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-fieldiq-dark/70"
                            >
                              {isCollapsed(type) ? (
                                <ChevronRight className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                              )}
                              <span className="text-xs font-black uppercase tracking-[0.1em] text-white">{type}</span>
                              <span className="text-xs tabular-nums text-fieldiq-muted">({items.length})</span>
                            </button>
                          </td>
                        </tr>
                        {!isCollapsed(type) && items.map(eq => {
                      const displayStatus = eq.status === 'reserved' ? 'reserved' : (eq.status === 'available' && (eq.currentJobId || eq.current_job_id)) ? 'reserved' : eq.status;
                      const reservedDate = eq.reserved_date || eq.reservedDate;
                      const reservedJob = eq.reserved_job_number || eq.reservedJobNumber;
                      const jobWell = eq.linked_job_well || eq.linkedJobWell;
                      const insp = getInspectionStatus(eq, timeZone);
                      const hasCert = !!eq.certDayLimit;
                      const locationLabel = getLocationLabel(eq, yardMap);
                      // api#985's latest assignment; the returned day is the TENANT's.
                      const lastJob = describeLastJob(parseLastJob(eq), timeZone);

                      return (
                        <tr
                          key={eq.id}
                          tabIndex={0}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            statusSkin(displayStatus).rail
                          }`}
                          onClick={() => navigate(`/equipment/${eq.id}`)}
                          onKeyDown={openOnRowKeys(eq.id)}
                        >
                          <td className="w-10 px-4 py-3" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(eq.id)}
                              onChange={e => {
                                setSelectedIds(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(eq.id);
                                  else next.delete(eq.id);
                                  return next;
                                });
                              }}
                              className="w-4 h-4 rounded border-fieldiq-border text-fieldiq-accent focus:ring-fieldiq-accent bg-fieldiq-dark cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-extrabold text-white">{eq.name}{eq.size ? ` - ${eq.size}` : ''}</p>
                              {(() => {
                                const parts = [eq.serialNumber, eq.manufacturer, eq.model, eq.capacity].filter(Boolean);
                                return parts.length > 0 ? (
                                  <p className="truncate text-sm text-fieldiq-muted">{parts.join(' · ')}</p>
                                ) : null;
                              })()}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={PILL}>{eq.type || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5">
                              <EquipmentStatusBadge status={displayStatus} />
                              {eq.status === 'left_on_site' && <AlertTriangle size={12} className="text-fi-amber" />}
                            </span>
                            {(displayStatus === 'reserved' || displayStatus === 'in_use' || displayStatus === 'left_on_site') && (reservedDate || reservedJob) && (
                              <p className="text-xs text-fieldiq-muted mt-1">
                                {reservedJob && <span>{reservedJob}</span>}
                                {reservedJob && reservedDate && <span> · </span>}
                                {reservedDate && <span>{formatCalendarDate(reservedDate, { month: 'short', day: 'numeric' })}</span>}
                                {jobWell && <span className="text-fieldiq-muted"> · {jobWell}</span>}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">
                            {locationLabel ? (
                              <span className="flex items-center gap-1">
                                <MapPin size={12} className={eq.status === 'left_on_site' ? 'text-fi-amber' : 'text-fieldiq-muted'} />
                                {locationLabel}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3" data-testid="last-job-cell">
                            {lastJob ? (
                              <div className="min-w-0">
                                <p className="truncate text-sm text-fieldiq-text" title={lastJob.summary}>{lastJob.summary}</p>
                                <p className={`text-xs ${lastJob.stillOut ? 'font-semibold text-fieldiq-accent-signal' : 'text-fieldiq-muted'}`}>{lastJob.state}</p>
                              </div>
                            ) : <span className="text-sm text-fieldiq-muted">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {hasCert && certLevelLabel(eq.certDescription, eq.certDayLimit) ? (
                              <span className="flex items-center gap-1 text-sm">
                                <Shield size={12} className="text-fi-amber" />
                                <span className="text-fieldiq-text">{certLevelLabel(eq.certDescription, eq.certDayLimit)}</span>
                              </span>
                            ) : <span className="text-sm text-fieldiq-muted">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {eq.certStartDate ? (
                              <span className="text-sm text-fieldiq-text">{formatCalendarDate(eq.certStartDate, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            ) : <span className="text-sm text-fieldiq-muted">—</span>}
                          </td>
                          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {/* Return, on the same rule the server enforces —
                                  `canReturnToYard`, shared with the field screen.
                                  This read `status === 'left_on_site'`, so the
                                  office could not bring back in_use gear at all,
                                  on a job of any status. Narrower than both the
                                  API and the field screen, and unnoticed because
                                  the field screen was the one being looked at. */}
                              {canReturnToYard(eq.status) && (
                                <button
                                  onClick={() => handlePickedUp(eq)}
                                  disabled={returnToYardMutation.isPending}
                                  className={`${ROW_ACTION} bg-fi-signal-green`}
                                >
                                  {returnToYardMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Truck size={12} />} Return
                                </button>
                              )}
                              {/* The other disposition. Return says the asset came
                                  home; this says it went straight to the next
                                  lease. Both are offered because both happen, and
                                  until now only the first one was reachable from
                                  the office. */}
                              {eq.status === 'left_on_site' && (
                                <button
                                  onClick={() => setJobTransferItem(eq)}
                                  disabled={transferToJobMutation.isPending}
                                  className={`${ROW_ACTION} bg-fi-signal-blue`}
                                >
                                  {transferToJobMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Forward size={12} />} To Job
                                </button>
                              )}
                              {insp && insp.color === 'text-red-400' && eq.status !== 'left_on_site' && (
                                <button className={`${ROW_ACTION} bg-fi-signal-amber`}>
                                  <Shield size={12} /> Inspect
                                </button>
                              )}
                              {/* Icon-only, and it had no accessible name at all —
                                  a screen reader announced "button" and a test
                                  could only reach it by hunting for an svg. Its
                                  siblings on this row already carry titles. */}
                              <Tooltip content="Edit">
                                <button
                                  onClick={() => { setEditItem(eq); setShowModal(true); }}
                                  aria-label="Edit"
                                  className={ROW_ICON}
                                >
                                  <Pencil size={12} />
                                </button>
                              </Tooltip>
                              {/* Retitled from a bare "Transfer" the moment a second
                                  transfer appeared on this row. This one edits
                                  LOCATION only -- it never moves the asset between
                                  jobs and never touches an assignment row, so
                                  reading it as the job handoff leaves the outgoing
                                  assignment open. */}
                              <Tooltip content="Move to shop or rig">
                                <button
                                  onClick={() => { setTransferItem(eq); setTransferYardId(''); }}
                                  className={ROW_ICON}
                                  aria-label="Move to shop or rig"
                                >
                                  <ArrowRightLeft size={12} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                          <td className="pr-3 py-3">
                            <ChevronRight size={14} className="text-fieldiq-muted" />
                          </td>
                        </tr>
                      );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile Cards — no rail here; that edge belongs to the
                surrounding register, never to a card. */}
            {!isLoading && filtered.length > 0 && (
            <div className="md:hidden space-y-4 p-4">
              {groupedEquipment.map(({ type, items }) => (
              <div key={type} className="space-y-3">
                {/* Group header — same collapse state as the desktop table */}
                <button
                  type="button"
                  onClick={() => toggleGroup(type)}
                  aria-expanded={!isCollapsed(type)}
                  className="w-full flex items-center gap-2 rounded-fi-control bg-fieldiq-dark/40 px-2 py-2 text-left"
                >
                  {isCollapsed(type) ? (
                    <ChevronRight className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                  )}
                  <span className="text-xs font-black uppercase tracking-[0.1em] text-white">{type}</span>
                  <span className="text-xs tabular-nums text-fieldiq-muted">({items.length})</span>
                </button>
                {!isCollapsed(type) && items.map(eq => {
                const displayStatus = eq.status === 'reserved' ? 'reserved' : (eq.status === 'available' && (eq.currentJobId || eq.current_job_id)) ? 'reserved' : eq.status;
                const reservedDate = eq.reserved_date || eq.reservedDate;
                const reservedJob = eq.reserved_job_number || eq.reservedJobNumber;
                const jobWell = eq.linked_job_well || eq.linkedJobWell;
                const insp = getInspectionStatus(eq, timeZone);
                const InspIcon = insp?.icon;
                const hasCert = !!eq.certDayLimit;
                const locationLabel = getLocationLabel(eq, yardMap);
                const lastJob = describeLastJob(parseLastJob(eq), timeZone);

                return (
                  <div
                    key={eq.id}
                    className="cursor-pointer overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card"
                    onClick={() => navigate(`/equipment/${eq.id}`)}
                  >
                    <div className="p-4">
                      {/* Header row */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-extrabold text-white">
                            {eq.name}{eq.type ? ` - ${eq.type}` : ''}
                            {eq.size ? ` - ${eq.size}` : ''}
                          </h3>
                          {(() => {
                            const parts = [eq.serialNumber, eq.manufacturer, eq.model, eq.capacity].filter(Boolean);
                            return parts.length > 0 ? (
                              <p className="text-sm text-fieldiq-muted truncate">{parts.join(' · ')}</p>
                            ) : null;
                          })()}
                        </div>
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <Tooltip content="Transfer">
                            <button
                              onClick={() => { setTransferItem(eq); setTransferYardId(''); }}
                              className={ROW_ICON}
                              aria-label="Transfer"
                            >
                              <ArrowRightLeft size={14} />
                            </button>
                          </Tooltip>
                          <Tooltip content="Edit">
                            <button
                              onClick={() => { setEditItem(eq); setShowModal(true); }}
                              aria-label="Edit"
                              className={ROW_ICON}
                            >
                              <Pencil size={14} />
                            </button>
                          </Tooltip>
                          <button
                            onClick={() => setDeleteConfirm(eq.id)}
                            className="rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-fi-red"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Status + location */}
                      <div className="flex items-center gap-2 mb-1">
                        <EquipmentStatusBadge status={displayStatus} />
                        {eq.status === 'left_on_site' && <AlertTriangle size={14} className="text-fi-amber" />}
                      </div>
                      {(displayStatus === 'reserved' || displayStatus === 'in_use' || displayStatus === 'left_on_site') && (reservedDate || reservedJob) && (
                        <p className="text-xs text-fieldiq-muted mb-2">
                          {reservedJob}{reservedJob && reservedDate ? ' · ' : ''}{reservedDate && formatCalendarDate(reservedDate, { month: 'short', day: 'numeric' })}
                          {jobWell && ` · ${jobWell}`}
                        </p>
                      )}

                      {/* Location line */}
                      {locationLabel && (
                        <div className="flex items-center gap-2 text-sm text-fieldiq-text mb-2">
                          <MapPin size={13} />
                          <span>{locationLabel}</span>
                        </div>
                      )}

                      {/* Last job (api#985) — same words as the desktop column. */}
                      {lastJob && (
                        <p className="text-xs text-fieldiq-muted mb-2" data-testid="last-job-card-line">
                          Last job: <span className="text-fieldiq-text">{lastJob.summary}</span> · <span className={lastJob.stillOut ? 'font-semibold text-fieldiq-accent-signal' : ''}>{lastJob.state}</span>
                        </p>
                      )}

                      {/* Cert day tracking badge */}
                      {hasCert && certLevelLabel(eq.certDescription, eq.certDayLimit) && (
                        <div className="flex items-center gap-2 text-sm mb-1">
                          <Shield size={14} className="text-fi-amber" />
                          <span className="text-fieldiq-text">{certLevelLabel(eq.certDescription, eq.certDayLimit)}</span>
                        </div>
                      )}

                      {/* Inspection status */}
                      {insp && InspIcon && (
                        <div className={`flex items-center gap-2 text-sm ${insp.color}`}>
                          <InspIcon size={14} />
                          <span>{insp.label}</span>
                        </div>
                      )}

                      {/* Working days since inspection. The `?? 0` this used to
                          carry turned an untracked tool into "0 / 500" — a full
                          budget it has not earned. Absent is a dash now. */}
                      {hasCert && (
                        <CertDayCounter
                          daysUsed={eq.workingDaysSinceInspection}
                          dayLimit={eq.certDayLimit}
                          mode="used"
                          size="xs"
                          barHeight="sm"
                          muted
                          className="mt-3"
                        />
                      )}
                    </div>

                    {/* Action buttons */}
                    <div onClick={e => e.stopPropagation()}>
                      {/* Same gate as the wide-screen row layout above, from
                          the same shared helper — two renderings of one asset must
                          not disagree about which actions it has. */}
                      {canReturnToYard(eq.status) && (
                        <button
                          onClick={() => handlePickedUp(eq)}
                          disabled={returnToYardMutation.isPending}
                          className="w-full py-3 bg-fi-signal-green text-fi-signal-ink font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                        >
                          {returnToYardMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Truck size={16} />} Return to Yard
                        </button>
                      )}
                      {eq.status === 'left_on_site' && (
                        <button
                          onClick={() => setJobTransferItem(eq)}
                          disabled={transferToJobMutation.isPending}
                          className="w-full py-3 bg-fi-signal-blue text-fi-signal-ink font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                        >
                          {transferToJobMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Forward size={16} />} Transfer to Job
                        </button>
                      )}
                      {insp && insp.color === 'text-red-400' && eq.status !== 'left_on_site' && (
                        <button
                          className="w-full py-3 bg-fi-signal-amber text-fi-signal-ink font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                        >
                          <Shield size={16} /> Log Inspection
                        </button>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
              ))}
            </div>
            )}
          </section>
        </div>
      </section>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-2">Delete Equipment?</h3>
            <p className="text-sm text-fieldiq-text mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 text-white py-2.5 rounded-lg text-sm font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      <ConfirmModal
        isOpen={bulkDeleteConfirm}
        title={`Delete ${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''}?`}
        body="Items not used on any job or ticket are removed permanently; items with history are archived and can be restored."
        confirmLabel="Delete"
        confirmVariant="destructive"
        loading={bulkDeleteMutation.isPending}
        onConfirm={() => {
          bulkDeleteMutation.mutate(Array.from(selectedIds));
          setBulkDeleteConfirm(false);
        }}
        onCancel={() => setBulkDeleteConfirm(false)}
      />

      {/* Transfer Modal */}
      {transferItem && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setTransferItem(null)}>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-1">Transfer Equipment</h3>
            <p className="text-sm text-fieldiq-text mb-4">
              Move <span className="text-white font-medium">{transferItem.serialNumber || transferItem.name}</span> to a shop or rig.
            </p>

            {/* Toggle: Shop vs Rig */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => { setTransferType('shop'); setTransferRigName(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${transferType === 'shop' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-dark text-fieldiq-text border border-fieldiq-border'}`}
              >
                Shop / Yard
              </button>
              <button
                onClick={() => { setTransferType('rig'); setTransferYardId(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${transferType === 'rig' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-dark text-fieldiq-text border border-fieldiq-border'}`}
              >
                Rig
              </button>
            </div>

            <div className="mb-5">
              {transferType === 'shop' ? (
                <>
                  <label className={labelClass}>Destination Shop</label>
                  <select
                    className={inputClass}
                    value={transferYardId}
                    onChange={e => setTransferYardId(e.target.value)}
                  >
                    <option value="">Select shop...</option>
                    {yards.map(y => (
                      <option key={y.id} value={y.id}>{y.name} — {y.city}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label className={labelClass}>Rig Name</label>
                  <input
                    className={inputClass}
                    value={transferRigName}
                    onChange={e => setTransferRigName(e.target.value)}
                    placeholder="e.g. Rig 47, PD 185"
                  />
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setTransferItem(null)} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium">Cancel</button>
              <button
                onClick={() => transferMutation.mutate({
                  id: transferItem.id,
                  ...(transferType === 'shop' ? { toYardId: transferYardId } : { toRig: transferRigName }),
                })}
                disabled={(transferType === 'shop' ? !transferYardId : !transferRigName.trim()) || transferMutation.isPending}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {transferMutation.isPending ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return to Yard Picker */}
      {jobTransferItem && (
        <JobTransferModal
          equipment={jobTransferItem}
          pending={transferToJobMutation.isPending}
          onClose={() => setJobTransferItem(null)}
          onConfirm={(destinationJobId, jobLabel, reason) =>
            transferToJobMutation.mutate({ id: jobTransferItem.id, destinationJobId, jobLabel, reason })
          }
        />
      )}

      {returnItem && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setReturnItem(null)}>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-1">Return Equipment</h3>
            <p className="text-sm text-fieldiq-text mb-4">
              Return <span className="text-white font-medium">{returnItem.serialNumber || returnItem.name}</span> to a yard.
            </p>
            <div className="mb-5">
              {/* Associated with its select. It was a bare <label> with no
                  htmlFor, so the control had no accessible name at all — the
                  field screen's equivalent picker has carried the association
                  since it was written. */}
              <label className={labelClass} htmlFor="return-yard">Destination Yard</label>
              <select
                id="return-yard"
                className={inputClass}
                value={returnYardId}
                onChange={e => setReturnYardId(e.target.value)}
              >
                <option value="">Select yard...</option>
                {yards.map(y => (
                  <option key={y.id} value={y.id}>{y.name} — {y.city}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setReturnItem(null)} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium">Cancel</button>
              <button
                onClick={() => returnToYardMutation.mutate({ id: returnItem.id, destinationYardId: returnYardId })}
                disabled={!returnYardId || returnToYardMutation.isPending}
                className="flex-1 bg-green-500 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-400 transition-colors"
              >
                {returnToYardMutation.isPending ? 'Returning...' : 'Confirm Return'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <AddEditModal
          item={editItem}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditItem(null); }}
        />
      )}

      {/* Import Modal */}
      {(importState === 'uploading' || importState === 'preview' || importState === 'saving') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-[95vw] mx-4 max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
              <h3 className="text-lg font-bold text-white">
                {importState === 'uploading' && 'Parsing Equipment List...'}
                {(importState === 'preview' || importState === 'saving') && 'Review Imported Equipment'}
              </h3>
              {importState !== 'uploading' && importState !== 'saving' && (
                <button onClick={resetImport} className="text-fieldiq-muted hover:text-white">
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Uploading spinner */}
            {importState === 'uploading' && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader2 size={40} className="animate-spin text-fieldiq-accent" />
                <p className="text-fieldiq-text">Parsing equipment list with AI...</p>
                <p className="text-fieldiq-muted text-xs">This may take a few seconds</p>
              </div>
            )}

            {/* Preview / Edit */}
            {(importState === 'preview' || importState === 'saving') && (
              <>
                {/* Top fields */}
                <div className="px-6 py-4 space-y-3 border-b border-fieldiq-border">
                  {importError && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
                      {importError}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label className="text-sm text-fieldiq-text block mb-1">Assign Home Shop</label>
                      <select
                        value={importHomeYardId}
                        onChange={e => setImportHomeYardId(e.target.value)}
                        className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white"
                        disabled={importState === 'saving'}
                      >
                        <option value="">No home shop</option>
                        {yards.map(y => (
                          <option key={y.id} value={y.id}>{y.name} — {y.city}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Items table */}
                <div className="flex-1 overflow-auto px-6 py-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-fieldiq-muted text-xs uppercase">
                        <th className="text-left pb-2 pr-2">Name</th>
                        <th className="text-left pb-2 pr-2">Type</th>
                        <th className="text-left pb-2 pr-2">Serial #</th>
                        <th className="text-left pb-2 pr-2">Manufacturer</th>
                        <th className="text-left pb-2 pr-2">Size</th>
                        <th className="text-left pb-2 pr-2">Capacity</th>
                        <th className="text-left pb-2 pr-2">Shop</th>
                        <th className="text-left pb-2 pr-2">Cert</th>
                        <th className="text-left pb-2 pr-2">Notes</th>
                        <th className="pb-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-fieldiq-border">
                      {importItems.map((item, i) => (
                        <tr key={i}>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.name}
                              onChange={e => updateImportItem(i, 'name', e.target.value)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[120px]"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <select
                              value={item.type}
                              onChange={e => updateImportItem(i, 'type', e.target.value)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-1.5 py-1 text-sm text-white w-full min-w-[110px]"
                              disabled={importState === 'saving'}
                            >
                              <option value="">Select</option>
                              {equipmentTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                              {item.type && !equipmentTypes.some(t => t.name === item.type) && (
                                <option value={item.type}>{item.type}</option>
                              )}
                            </select>
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.serialNumber || ''}
                              onChange={e => updateImportItem(i, 'serialNumber', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[80px]"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.manufacturer || ''}
                              onChange={e => updateImportItem(i, 'manufacturer', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[80px]"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.size || ''}
                              onChange={e => updateImportItem(i, 'size', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[70px]"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.capacity || ''}
                              onChange={e => updateImportItem(i, 'capacity', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[80px]"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <select
                              value={item.homeYardId || ''}
                              onChange={e => updateImportItem(i, 'homeYardId', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-1.5 py-1 text-sm text-white w-full min-w-[100px]"
                              disabled={importState === 'saving'}
                            >
                              <option value="">—</option>
                              {yards.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                            </select>
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.certDescription || ''}
                              onChange={e => updateImportItem(i, 'certDescription', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[80px]"
                              placeholder={item.certDayLimit ? `${item.certDayLimit} days` : ''}
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <input
                              type="text"
                              value={item.notes || ''}
                              onChange={e => updateImportItem(i, 'notes', e.target.value || null)}
                              className="bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-white w-full min-w-[80px]"
                              disabled={importState === 'saving'}
                            />
                          </td>
                          <td className="py-1.5">
                            <button
                              onClick={() => removeImportItem(i)}
                              className="text-fieldiq-muted hover:text-red-400"
                              disabled={importState === 'saving'}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importItems.length === 0 && (
                    <p className="text-center text-fieldiq-muted text-sm py-8">
                      No items parsed. Try a different file.
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-fieldiq-border">
                  <p className="text-fieldiq-muted text-xs">
                    {importItems.length} item{importItems.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={resetImport}
                      className="px-4 py-2 text-sm text-fieldiq-text hover:text-white transition-colors"
                      disabled={importState === 'saving'}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmImport}
                      disabled={importState === 'saving' || importItems.length === 0}
                      className="flex items-center gap-2 px-5 py-2 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importState === 'saving' && <Loader2 size={14} className="animate-spin" />}
                      {importState === 'saving' ? 'Saving...' : 'Confirm & Save'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// ADD / EDIT MODAL
// ==========================================
function AddEditModal({ item, onSave, onClose }: { item: Equipment | null; onSave: (e: Equipment, certCycleAction?: 'carry' | 'reset', certChangeReason?: string) => Promise<string | null>; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { isMetric } = useLocale();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Who may TYPE OVER a certification counter or cycle start; the API answers
  // 403 to everyone else. See canWriteCertFields.
  const certWritable = canWriteCertFields(user?.role);
  // Upload and Remove on the certificate PDF: a wider list than the counter
  // (supervisor is on it). See canManageEquipmentCertDocuments.
  const certDocsWritable = canManageEquipmentCertDocuments(user?.role);

  // ── Moving the cert start date is two different intentions ────────────────
  //
  // RESET (the tool went through inspection, the new cycle starts at nought) or
  // CARRY (the date was wrong and is being corrected). The server cannot tell
  // which from the payload and guessing `reset` ERASES a compliance count, so
  // the client says. Same question, same wording, as the equipment DETAIL
  // modal — the two edit doors onto this column must not ask it differently.
  //
  // The API defaults to `carry` with a logged warning when the field is absent,
  // for one release. That is a migration aid for older clients, not a contract.
  const initialCertStart = calendarDayString(item?.certStartDate) ?? String(item?.certStartDate ?? '');
  const priorCounter: number | null = item?.workingDaysSinceInspection ?? null;
  const [certCycleAction, setCertCycleAction] = useState<'carry' | 'reset'>('carry');

  /**
   * Why the certification day count is being changed.
   *
   * REQUIRED whenever the counter actually moves to a number, because API 0388
   * turned this box from a silent overwrite into an append-only ledger row
   * (`counter_set`) — and that row exists to answer "who said so, and why".
   * The server refuses a blank one at the route AND with a CHECK constraint;
   * this blocks submit so the person is not told after the fact.
   *
   * It is NOT required to CLEAR the counter. A null counter means "not day
   * counted", which is a statement about the unit rather than an assertion
   * about a number, and the ledger holds integers — there is no row to write.
   *
   * `certCycleAction` next to it defaults to a BEHAVIOUR when absent. This
   * cannot: defaulting an explanation would write a placeholder sentence onto
   * the very units whose counts are unexplained, permanently, into a table
   * with no UPDATE and no DELETE.
   */
  const [certChangeReason, setCertChangeReason] = useState('');

  /*
   * Who may add a type, as opposed to who may add equipment. The two are NOT
   * the same permission and that is the whole bug:
   *
   *   POST /equipment        authMiddleware only  — no role gate at all
   *   POST /equipment-types  requireRole('admin')
   *
   * So a dispatcher, supervisor, office_staff or logistics user opens this
   * modal legitimately, creates equipment all day, and is refused only on the
   * type. Withdrawn rather than greyed out, per PR #421 and the switch this
   * same file gates a thousand lines up.
   *
   * Literal 'admin' matches src/lib/roles.ts:155-163 rather than the divergent
   * gate in ApprovalQueueScreen: `dispatcher_admin_access` is not on /auth/me,
   * and under-granting is cheap here — an elevated dispatcher on Dash or Smith
   * loses a shortcut inside this modal, not a workflow, because both tenants
   * have an admin who can add the type, and every existing type stays
   * selectable.
   */
  const isAdmin = user?.role === 'admin';
  const { data: yardsData } = useQuery({
    queryKey: ['service-locations'],
    queryFn: () => api.serviceLocations.list().then(r => r.data.data ?? r.data ?? []),
  });
  const yards: ServiceLocation[] = yardsData ?? [];

  const { data: equipmentTypesData } = useQuery<EquipmentType[]>({
    queryKey: ['equipment-types'],
    queryFn: () => api.equipmentTypes.list().then(r => r.data.data ?? r.data ?? []),
  });
  const eqTypes: EquipmentType[] = (equipmentTypesData ?? [])
    .filter(t => t.isActive !== false)
    .sort((a, b) => a.isStandard === b.isStandard ? a.name.localeCompare(b.name) : a.isStandard ? -1 : 1);

  /*
   * The catalog INCLUDING deactivated rows, so this modal can name the type a
   * unit is already filed under even after the tenant withdrew it. The picker
   * still offers `eqTypes` — see `typeOptions` below, which adds back exactly
   * one row and only for the unit being edited.
   *
   * Same key the Equipment Types settings page uses, deliberately: it is the
   * same request, so React Query serves whichever screen asked second from
   * cache, and the prefix invalidation that page already fires on every write
   * refreshes this too.
   */
  const { data: allTypesData } = useQuery<CatalogEquipmentType[]>({
    queryKey: ['equipment-types', 'all'],
    queryFn: () =>
      api.equipmentTypes.list(true).then(r =>
        unwrapArray<any>(r.data)
          .map(toEquipmentType)
          .filter((t): t is CatalogEquipmentType => t !== null)
          .sort(compareEquipmentTypes)
      ),
  });

  // Does moving THIS asset to `to` owe a disposition the API will refuse?
  //
  // Evaluated against `item` — the SAVED row — not `form`. The question is
  // where the asset is, not what the modal is mid-way through choosing; reading
  // `form` would let picking "In Maintenance" re-evaluate against the value
  // being picked and unblock itself.
  const statusOwesDisposition = (to: string) => !!item && owesDisposition(item, to);
  const dispositionNote = item && isDeployedOnLiveJob(item)
    ? dispositionMessage([item])
    : '';

  const [form, setForm] = useState<Equipment>(item
    ? {
        ...item,
        assetKind: item.assetKind || item.asset_kind || 'equipment',
        licensePlate: item.licensePlate ?? item.license_plate ?? '',
        vin: item.vin ?? '',
      }
    : {
        id: '', name: '', type: '', equipmentTypeId: '', serialNumber: '', manufacturer: '', model: '', size: '', capacity: '', status: 'available',
        assetKind: 'equipment', licensePlate: '', vin: '',
      });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  // Which cert level the user PICKED, as opposed to which one the day limit
  // happens to look like. Custom cannot be derived — a custom limit of exactly
  // 250 is indistinguishable from Level 3 — so clicking Custom had nothing to
  // write and did nothing at all. This is that missing state. The modal mounts
  // fresh per open, so it resets with the form.
  const [certLevelOverride, setCertLevelOverride] = useState<'custom' | null>(null);

  // "Add New Type" state
  const [newTypeName, setNewTypeName] = useState('');
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [isSavingType, setIsSavingType] = useState(false);

  /*
   * "Manage types" leaves the app's current route, which unmounts this modal
   * and takes the half-filled form with it. Nothing else in this modal can
   * discard the form, so leaving silently is not a cost the user has any way
   * to anticipate — hence the snapshot and the confirm.
   *
   * Snapshotting the INITIAL form rather than testing fields for emptiness
   * covers the edit case too, where every field is prefilled and "non-empty"
   * would mean "dirty" the instant the modal opened.
   *
   * In-app navigation rather than a new tab: inside the native build a
   * `target="_blank"` opens the system browser, which has no session, so the
   * link would land on a login screen instead of the page it names.
   */
  const initialFormRef = useRef<string>('');
  if (!initialFormRef.current) initialFormRef.current = JSON.stringify(form);
  const goManageTypes = () => {
    const dirty = JSON.stringify(form) !== initialFormRef.current;
    if (dirty && !window.confirm('Leave and lose the details entered here?')) return;
    navigate('/settings/equipment-types');
  };

  // Shared helper: POST the type name, wait for the dropdown to refresh, then select it.
  // Returns the resolved type (with id + canonical name) or null on failure.
  const saveAndSelectType = async (name: string): Promise<{ id: string; name: string } | null> => {
    setIsSavingType(true);
    try {
      const res = await api.equipmentTypes.create({ name });
      const created = res.data?.data ?? res.data;
      if (!created?.id) return null;
      // Wait for the dropdown option list to include the new type before selecting it
      await queryClient.invalidateQueries({ queryKey: ['equipment-types'] });
      setForm(f => ({ ...f, equipmentTypeId: created.id, type: created.name }));
      setNewTypeName('');
      setShowNewTypeInput(false);
      toast.success('Equipment type added');
      return created;
    } catch (err: any) {
      /*
       * POST answers 409 for two different situations and the generic toast
       * threw away which one. The one that matters is a name held by a
       * DEACTIVATED type: this dropdown only carries active ones, so there is
       * nothing on screen to point at, the admin retypes the same name, gets
       * the same refusal, and concludes the feature is broken — while the type
       * they want is one PATCH away on the settings page.
       *
       * Telling the two apart means matching a substring of a human-readable
       * message that the API could reword at any time without anything here
       * failing, so the fallback carries the weight: an unmatched 409 still
       * reports a duplicate, which is true either way.
       */
      const apiError = String(err?.response?.data?.error ?? '');
      if (err?.response?.status === 409 && /deactivated/i.test(apiError)) {
        toast.error(`“${name}” exists but is deactivated. Reactivate it under Settings → Equipment Types.`);
      } else if (err?.response?.status === 409) {
        toast.error(`“${name}” already exists.`);
      } else {
        toast.error('Failed to create equipment type');
      }
      return null;
    } finally {
      setIsSavingType(false);
    }
  };

  const createTypeMutation = useMutation({
    mutationFn: (name: string) => saveAndSelectType(name).then(r => { if (!r) throw new Error('failed'); return r; }),
  });

  // Resolve equipmentTypeId from type name for existing equipment that predates the equipmentTypeId field
  useEffect(() => {
    if (item && !form.equipmentTypeId && form.type && eqTypes.length > 0) {
      const match = eqTypes.find(t => t.name.toLowerCase() === form.type.toLowerCase());
      if (match) {
        setForm(f => ({ ...f, equipmentTypeId: match.id }));
      }
    }
  }, [eqTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cert PDF upload state
  const certFileRef = useRef<HTMLInputElement>(null);
  const [certUploading, setCertUploading] = useState(false);
  const [certUpload, setCertUpload] = useState<{ id: string; originalName: string; url: string } | null>(null);

  // Load existing cert upload when editing
  const isEditing = !!item?.id;
  const { data: existingCertData } = useQuery({
    queryKey: ['cert-uploads', item?.id],
    queryFn: () => api.uploads.listForEntity('equipment_cert', item!.id).then(r => {
      const uploads = r.data.data ?? r.data ?? [];
      return uploads.length > 0 ? uploads[uploads.length - 1] : null;
    }),
    enabled: isEditing,
  });

  useEffect(() => {
    if (existingCertData) {
      setCertUpload({ id: existingCertData.id, originalName: existingCertData.originalName || existingCertData.original_name || 'Certificate.pdf', url: existingCertData.url });
    }
  }, [existingCertData]);

  const handleCertUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !item?.id) return;
    setCertUploading(true);
    try {
      // Step 1: Get presigned URL
      const { data: presigned } = await api.uploads.getPresignedUrl({
        fileName: file.name,
        contentType: file.type || 'application/pdf',
        entityType: 'equipment_cert',
        entityId: item.id,
      });
      // Step 2: Upload to S3
      await putToPresignedUrl(presigned.uploadUrl, file, file.type || 'application/pdf');
      // Step 3: Confirm
      await api.uploads.confirm({ key: presigned.key, size: file.size });
      // Refresh cert uploads
      const refreshed = await api.uploads.listForEntity('equipment_cert', item.id);
      const uploads = refreshed.data.data ?? refreshed.data ?? [];
      if (uploads.length > 0) {
        const latest = uploads[uploads.length - 1];
        setCertUpload({ id: latest.id, originalName: latest.originalName || latest.original_name || file.name, url: latest.url });
      }
      queryClient.invalidateQueries({ queryKey: ['cert-uploads', item.id] });
    } catch (err: any) {
      console.error('Cert upload failed:', err);
      alert(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setCertUploading(false);
      if (certFileRef.current) certFileRef.current.value = '';
    }
  };

  // Remove deletes the ONE document on screen — `certUpload`, the latest
  // equipment_cert upload — by its id. It used to list every equipment_cert
  // upload on the unit and delete them all, which could also take a
  // pressure-control certificate's document filed under the same type. The
  // API refuses (409) a file a record still holds; that is said under the
  // certificate, where it stays.
  const [certRemoveError, setCertRemoveError] = useState('');
  const handleCertRemove = async () => {
    if (!item?.id || !certUpload?.id) return;
    setCertRemoveError('');
    try {
      await api.uploads.delete(certUpload.id);
      setCertUpload(null);
      queryClient.invalidateQueries({ queryKey: ['cert-uploads', item.id] });
    } catch (err: any) {
      console.error('Cert delete failed:', err);
      const message = err?.response?.data?.error;
      if (err?.response?.status === 409) {
        setCertRemoveError(message || 'This certificate belongs to another record and cannot be removed here.');
      } else {
        toast.error(message || 'Could not remove the certificate');
      }
      queryClient.invalidateQueries({ queryKey: ['cert-uploads', item.id] });
    }
  };

  const certTrackingEnabled = !!form.certDayLimit || !!form.certDescription || !!form.certStartDate;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serialError, setSerialError] = useState<string | null>(null);

  /*
   * What the Type picker may offer: the live catalog, plus this unit's own
   * current type when the catalog no longer carries it. A `<select>` cannot
   * display a value it has no option for, so without the second half a unit
   * filed under a deactivated type opens showing "Select type" — and one touch
   * of the control reassigns it with nothing having said it already had a type.
   * See `buildEquipmentTypeOptions` for why that is not cosmetic.
   */
  const typeOptions = buildEquipmentTypeOptions(
    eqTypes,
    form.equipmentTypeId,
    allTypesData ?? [],
    // The unit's own copy of the name, for the window where the include-inactive
    // request has not landed. `equipmentTypeName` is the catalog join; `type` is
    // the text column the 0334 trigger derives from it.
    form.equipmentTypeName ?? form.type,
  );
  const selectedTypeOption = typeOptions.find(o => o.id === form.equipmentTypeId) ?? null;

  /*
   * Mirrors the Save button's disabled condition exactly, so the explanation
   * and the block cannot drift apart.
   *
   * Keyed on whether the CONTROL HAS A SELECTED OPTION, not on whether the id
   * is falsy. Those came apart the moment a type could be deactivated out from
   * under a unit: the id is set, so a falsy test reads "a type is chosen", while
   * the control renders a blank placeholder and says the opposite. Asking the
   * option list means the message on screen and the state behind it cannot
   * disagree — whatever the reason an id fails to resolve.
   *
   * MEASURED, SO IT IS NOT OVERSOLD: reverting this line and the Save button's
   * to `!form.equipmentTypeId` reddens ZERO of the ten tests in
   * `EquipmentScreen.inactive-type-option.test.tsx`. With the union above in
   * place the two conditions agree on every reachable input, because a truthy
   * id always yields an option. So this is not a second, independently tested
   * guard — it is the same guard stated in terms of the thing the user is
   * looking at, so the two cannot drift if the option list ever grows a case
   * where an id resolves to nothing. Do not read the green suite as coverage of
   * this line.
   */
  const typeNeedsChoosing = !!item && !selectedTypeOption && !newTypeName.trim();

  // Calendar-day comparison on both sides. The stored value can arrive as
  // `2026-01-01T00:00:00.000Z` while the picker holds `2026-01-01`, and a raw
  // string compare would call that a move.
  const certStartMoved =
    !!item && (calendarDayString(form.certStartDate) ?? String(form.certStartDate ?? '')) !== initialCertStart;
  // `!== null`, not a falsy check: a counter of 0 is a real count, and carrying
  // 0 versus resetting to 0 differ in whether a cycle_reset row is written.
  const askCertCycle = certWritable && certStartMoved && priorCounter !== null;

  // Derived here rather than beside the reason state, because `form` is
  // declared below that point. Same change-not-presence rule as certStartMoved
  // above: the modal prefills the counter and PUTs the whole form back, so only
  // a real move asks for an explanation.
  const counterInForm: number | null =
    form.workingDaysSinceInspection == null ? null : Number(form.workingDaysSinceInspection);
  const counterMoved = counterInForm !== null && counterInForm !== priorCounter;
  const counterReasonMissing = counterMoved && certChangeReason.trim() === '';
  const cycleArg = () => (askCertCycle ? certCycleAction : undefined);
  /** The reason, sent only when the counter actually moved to a number. */
  const reasonArg = () => (counterMoved ? certChangeReason.trim() : undefined);

  const handleSubmit = async () => {
    if (!form.name) return;
    // Defence in depth: Save is `disabled` under exactly this condition. The
    // user-visible half is the required-marked input and the disabled button;
    // this is here so a programmatic submit cannot skip the explanation.
    if (counterReasonMissing) return;
    setSerialError(null);

    // Auto-save pending new type on submit (forgiving UX)
    if (!form.equipmentTypeId && newTypeName.trim()) {
      setIsSubmitting(true);
      const resolved = await saveAndSelectType(newTypeName.trim());
      if (!resolved) { setIsSubmitting(false); return; } // POST failed; toast already shown
      const err = await onSave({ ...form, equipmentTypeId: resolved.id, type: resolved.name } as Equipment, cycleArg(), reasonArg());
      if (err) setSerialError(err);
      setIsSubmitting(false);
      return;
    }

    // Defence in depth only. The Save button is `disabled` under exactly this
    // condition, so this cannot be reached by clicking it — see
    // `typeNeedsChoosing` below, which is where the user-visible half lives.
    if (!form.equipmentTypeId) return;
    setIsSubmitting(true);
    const err = await onSave(form, cycleArg(), reasonArg());
    if (err) setSerialError(err);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-xl w-full max-w-md max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-fieldiq-border flex-shrink-0">
          <h2 className="text-lg font-bold text-white">{item ? 'Edit Equipment' : 'Add Equipment'}</h2>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Asset type — Equipment | Vehicle */}
          <div>
            <label className={labelClass}>Asset type</label>
            <div className="flex gap-2">
              {(['equipment', 'vehicle'] as const).map(kind => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => set('assetKind', kind)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    (form.assetKind || 'equipment') === kind
                      ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-fieldiq-text border-fieldiq-border hover:border-fieldiq-accent'
                  }`}
                >
                  {kind === 'equipment' ? 'Equipment' : 'Vehicle'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Name *</label>
            <input className={inputClass} placeholder="e.g. Power Tongs" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Type *</label>
            {/* The label above is a sibling, not an `htmlFor` target, so this
                control had no accessible name — it announced as an unlabelled
                combobox and could only be reached positionally. */}
            <select
              aria-label="Type"
              className={inputClass}
              value={selectedTypeOption?.id ?? ''}
              onChange={e => {
                /*
                 * Resolve against `typeOptions`, not `eqTypes`. Re-picking the
                 * off-list option is a legal no-op the user is entitled to make,
                 * and resolving it against the active catalog alone would miss —
                 * blanking `type` on a unit that has one, which is the bug this
                 * option exists to prevent, arriving through the fix for it.
                 */
                const picked = typeOptions.find(t => t.id === e.target.value);
                setForm(f => ({ ...f, equipmentTypeId: e.target.value, type: picked?.name || '' }));
              }}
            >
              <option value="">Select type</option>
              {typeOptions.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {typeNeedsChoosing && (
              /* SAVE IS DISABLED RIGHT NOW AND NOTHING SAID SO.
                 On a row whose equipment_type_id is null, this select opens
                 blank while the list behind it shows a type — because the list
                 renders the free text and the select is bound to the id. The
                 Save button is disabled by that same missing id, with no
                 explanation, so the edit cannot be completed and nothing
                 accounts for it. 15 rows are deliberately in that state pending
                 a tenant decision, and 147 were before the 0335 backfill.
                 Explaining it up front beats erroring after a click that the
                 disabled button never delivers. */
              <p className="text-amber-400 text-xs mt-1.5">
                {form.type
                  ? `This item is filed as “${form.type}”, which is not in the type list. Choose a type to save.`
                  : 'Choose a type to save.'}
              </p>
            )}
            {!isAdmin ? (
              /* Say what happened rather than leaving nothing where a control
                 was. A non-admin who needs a type the catalog does not carry
                 gets a path forward; silence alone would read as the catalog
                 being complete. */
              <p className="text-xs text-fieldiq-muted mt-1.5">
                Ask an admin to add a type that is not listed.
              </p>
            ) : !showNewTypeInput ? (
              /* Two affordances, deliberately both. Creating one inline is the
                 right move mid-task and it is what tenants actually use; the
                 link is for renaming, hiding and removing, none of which
                 belong inside a modal about a single piece of equipment. */
              <div className="flex items-center gap-3 mt-1.5">
                <button
                  type="button"
                  onClick={() => setShowNewTypeInput(true)}
                  className="flex items-center gap-1 text-xs text-fieldiq-accent hover:text-amber-400 transition-colors"
                >
                  <Plus size={12} /> Add new type
                </button>
                <button
                  type="button"
                  onClick={goManageTypes}
                  className="text-xs text-fieldiq-muted hover:text-white transition-colors"
                >
                  Manage types →
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="text"
                  value={newTypeName}
                  onChange={e => setNewTypeName(e.target.value)}
                  placeholder="Type name..."
                  className="flex-1 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2.5 py-1.5 text-white text-xs focus:border-fieldiq-accent focus:outline-none"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newTypeName.trim()) saveAndSelectType(newTypeName.trim());
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => { if (newTypeName.trim()) saveAndSelectType(newTypeName.trim()); }}
                  disabled={!newTypeName.trim() || isSavingType}
                  className="text-fieldiq-accent hover:text-amber-400 disabled:opacity-40 transition-colors"
                >
                  {isSavingType ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowNewTypeInput(false); setNewTypeName(''); }}
                  className="text-fieldiq-muted hover:text-white transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
          <div>
            <label className={labelClass}>Serial Number / Equipment ID</label>
            <input className={`${inputClass}${serialError ? ' border-red-500 focus:border-red-500' : ''}`} placeholder="e.g. Tongs-05" value={form.serialNumber || ''} onChange={e => { set('serialNumber', e.target.value); setSerialError(null); }} />
            {serialError && (
              <p className="text-red-400 text-xs mt-1">{serialError}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>Manufacturer</label>
            <input className={inputClass} placeholder="e.g. Eckel, BJ, McCoy" value={form.manufacturer || ''} onChange={e => set('manufacturer', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Model</label>
            <input className={inputClass} placeholder="e.g. RT-65, HT-100" value={form.model || ''} onChange={e => set('model', e.target.value)} />
          </div>
          {form.assetKind === 'vehicle' && (
            <>
              <div>
                <label className={labelClass}>License plate</label>
                <input className={inputClass} placeholder="e.g. ABC-1234" value={form.licensePlate || ''} onChange={e => set('licensePlate', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>VIN</label>
                <input className={inputClass} placeholder="17-character VIN" maxLength={17} value={form.vin || ''} onChange={e => set('vin', e.target.value.toUpperCase())} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Odometer ({isMetric ? 'km' : 'mi'})</label>
                  <input className={inputClass} type="number" min={0} placeholder="e.g. 125000" value={(form as any).odometer ?? ''} onChange={e => set('odometer', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Engine hours</label>
                  <input className={inputClass} type="number" min={0} placeholder="e.g. 4500" value={(form as any).engineHours ?? ''} onChange={e => set('engineHours', e.target.value)} />
                </div>
              </div>
            </>
          )}
          {form.assetKind !== 'vehicle' && (
            <>
              <div>
                <label className={labelClass}>Size</label>
                <input className={inputClass} placeholder="e.g. 13-3/8&quot;, 4-1/2&quot; - 9-5/8&quot;" value={form.size || ''} onChange={e => set('size', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Capacity</label>
                <input className={inputClass} placeholder="e.g. 50,000 ft/lbs, 150T" value={form.capacity || ''} onChange={e => set('capacity', e.target.value)} />
              </div>
            </>
          )}
          <div>
            <label className={labelClass}>Status</label>
            {/*
              `item`, not `form` — the question is where the asset IS, which is
              the saved row, not what this modal is mid-way through choosing.
              Reading `form` would let picking "In Maintenance" re-evaluate the
              rule against the value being picked and unblock itself.
            */}
            <select
              className={inputClass}
              value={form.status}
              onChange={e => set('status', e.target.value)}
            >
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="in_use">In Use</option>
              <option value="left_on_site">On Location</option>
              <option value="maintenance" disabled={statusOwesDisposition('maintenance')}>
                In Maintenance{statusOwesDisposition('maintenance') ? ' — still out on a job' : ''}
              </option>
              <option value="retired" disabled={statusOwesDisposition('retired')}>
                Retired{statusOwesDisposition('retired') ? ' — still out on a job' : ''}
              </option>
            </select>
            {dispositionNote && (
              <p className="mt-1.5 text-xs text-fieldiq-accent" role="note">{dispositionNote}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Home Shop</label>
            <select className={inputClass} value={form.homeYardId || ''} onChange={e => set('homeYardId', e.target.value || undefined)}>
              <option value="">{yards.length === 0 ? 'No shops added yet' : 'No home shop'}</option>
              {yards.map(y => (
                <option key={y.id} value={y.id}>{y.name} — {y.city}</option>
              ))}
            </select>
          </div>

          {form.assetKind !== 'vehicle' && (
            <div className="bg-fieldiq-dark rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={certTrackingEnabled}
                  onChange={e => {
                    if (!e.target.checked) {
                      setForm(f => ({ ...f, certDayLimit: undefined, certStartDate: undefined, certExpiryDate: undefined, certDescription: undefined }));
                    } else {
                      setForm(f => ({ ...f, certDayLimit: 500, certDescription: 'CAODC Level 4' }));
                    }
                  }}
                  className="w-5 h-5 rounded border-fieldiq-border text-fieldiq-accent focus:ring-fieldiq-accent bg-fieldiq-card"
                />
                <div>
                  <span className="text-sm font-medium text-white">Inspections</span>
                  <p className="text-sm text-fieldiq-muted">Working day limit and inspection certificate</p>
                </div>
              </label>

              {certTrackingEnabled && (
                <div className="mt-4 space-y-3 border-t border-fieldiq-border pt-4">
                  {/* Preset buttons */}
                  <div>
                    <label className={labelClass}>Certification Level</label>
                    <div className="grid grid-cols-3 gap-2">
                      {CERT_LEVEL_PRESETS.map(preset => {
                        const isActive = preset.days
                          ? certLevelOverride !== 'custom' && form.certDayLimit === preset.days
                          : certLevelOverride === 'custom' || !isPresetDayLimit(form.certDayLimit);
                        return (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              if (preset.days) {
                                setCertLevelOverride(null);
                                setForm(f => ({ ...f, certDayLimit: preset.days!, certDescription: preset.label }));
                              } else {
                                // Custom: keep the current day limit — the
                                // Working Day Limit field below is editable and
                                // the user types their own number — but stamp
                                // the description, or the record keeps whatever
                                // level was last selected and every read-only
                                // screen prints that stale level forever.
                                setCertLevelOverride('custom');
                                setForm(f => ({ ...f, certDescription: preset.label }));
                              }
                            }}
                            className={`py-2 rounded-lg text-xs font-medium transition ${
                              isActive ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-text'
                            }`}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Description</label>
                    <input className={inputClass} placeholder="e.g. Annual Working Day Certification" value={form.certDescription || ''} onChange={e => set('certDescription', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Working Day Limit</label>
                    {/* A cleared field is "not tracked", not a limit of 0. Number('') is 0,
                        and 0 used to reach the API, which stored it — three live vehicles
                        carried cert_day_limit = 0 that way (API #498). Absent is not zero. */}
                    <input className={inputClass} type="number" min={1} placeholder="500" value={form.certDayLimit || ''} onChange={e => set('certDayLimit', e.target.value === '' ? undefined : Number(e.target.value))} />
                  </div>
                  {certWritable && (
                    <div>
                      <label className={labelClass}>Cert Start Date</label>
                      <DatePicker value={form.certStartDate || ''} onChange={v => set('certStartDate', v)} />
                    </div>
                  )}
                  {askCertCycle && (
                    <div className="bg-fieldiq-dark rounded-lg border border-amber-500/40 p-3">
                      <p className="text-xs text-white font-medium">
                        This tool has {priorCounter} certification day{priorCounter === 1 ? '' : 's'} counted.
                        What should happen to that count?
                      </p>
                      <div className="mt-2 space-y-2">
                        {([
                          ['carry', 'Keep the count', 'The start date was wrong and is being corrected. The count means the same thing.'],
                          ['reset', 'Start the new cycle at zero', 'The tool went through inspection. Days worked under the old certificate stop counting.'],
                        ] as const).map(([value, title, detail]) => (
                          <label key={value} className="flex items-start gap-2.5 cursor-pointer">
                            <input
                              type="radio" name="certCycleAction" value={value}
                              checked={certCycleAction === value}
                              onChange={() => setCertCycleAction(value)}
                              className="mt-0.5 accent-amber-500"
                            />
                            <span>
                              <span className="block text-sm text-white">{title}</span>
                              <span className="block text-xs text-fieldiq-muted">{detail}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                      {certCycleAction === 'reset' && (
                        <p className="mt-2 text-xs text-amber-300">
                          A reset is recorded in the certification log as an entry of
                          −{priorCounter} day{priorCounter === 1 ? '' : 's'}. Nothing is deleted.
                        </p>
                      )}
                    </div>
                  )}
                  {/* Expiry is not gated — the API only guards the start date
                      and the counter. These are stacked blocks here, not a
                      grid, so hiding one leaves no hole to fill. */}
                  <div>
                    <label className={labelClass}>Cert Expiry Date</label>
                    <DatePicker value={form.certExpiryDate || ''} onChange={v => set('certExpiryDate', v)} />
                  </div>
                  {certWritable && (
                    <div>
                      <label className={labelClass}>Working Days Since Last Inspection</label>
                      <input className={inputClass} type="number" min={0} placeholder="0" value={form.workingDaysSinceInspection ?? ''} onChange={e => set('workingDaysSinceInspection', e.target.value ? Number(e.target.value) : null)} />
                      <p className="text-xs text-fieldiq-muted mt-1">Manually entered — current working days on this unit since last inspection</p>
                      {/* Shown only once the number has actually MOVED. The
                          modal prefills this box and PUTs the whole form back,
                          so demanding a reason on every save would ask for one
                          on edits that never touch the count — the same
                          change-not-presence rule the API's gate uses. */}
                      {counterMoved && (
                        <div className="mt-3 bg-fieldiq-dark rounded-lg border border-amber-500/40 p-3">
                          <label className={labelClass} htmlFor="cert-change-reason">
                            Why is the count changing? <span className="text-amber-400">Required</span>
                          </label>
                          <input
                            id="cert-change-reason" className={inputClass}
                            placeholder="e.g. Recount against log book 3"
                            value={certChangeReason}
                            onChange={e => setCertChangeReason(e.target.value)}
                          />
                          <p className="text-xs text-fieldiq-muted mt-1.5">
                            {priorCounter === null
                              ? `Setting this count to ${counterInForm}.`
                              : `Changing this count from ${priorCounter} to ${counterInForm}.`}
                            {' '}It is recorded in the certification log with your name and cannot be edited afterwards.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Inspection Certificate PDF */}
                  {(certDocsWritable || certUpload) && (
                  <div>
                    <label className={labelClass}>Inspection Certificate (PDF)</label>
                    {!isEditing ? (
                      <p className="text-sm text-fieldiq-muted italic">Save equipment first, then upload certificate</p>
                    ) : certUploading ? (
                      <div className="flex items-center gap-2 text-sm text-fieldiq-text">
                        <Loader2 size={14} className="animate-spin" /> Uploading...
                      </div>
                    ) : certUpload ? (
                      <div className="flex items-center gap-2 bg-fieldiq-card rounded-lg px-3 py-2 border border-fieldiq-border">
                        <FileText size={16} className="text-green-400 flex-shrink-0" />
                        <span className="text-sm text-white truncate flex-1">{certUpload.originalName}</span>
                        <Tooltip content="View">
                          <button type="button" onClick={() => {
                            void openUploadDownload(certUpload.id);
                          }} className="text-fieldiq-accent hover:text-amber-300" aria-label="View">
                            <ExternalLink size={14} />
                          </button>
                        </Tooltip>
                        {certDocsWritable && (
                          <Tooltip content="Remove">
                            <button type="button" onClick={handleCertRemove} className="text-fieldiq-muted hover:text-red-400" aria-label="Remove">
                              <X size={14} />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      <>
                        <input ref={certFileRef} type="file" accept="application/pdf" className="hidden" onChange={handleCertUpload} />
                        <button
                          type="button"
                          onClick={() => certFileRef.current?.click()}
                          className="w-full border-2 border-dashed border-fieldiq-border rounded-lg py-3 text-sm text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors flex items-center justify-center gap-2"
                        >
                          <Upload size={16} /> Upload Certificate PDF
                        </button>
                      </>
                    )}
                    {certRemoveError && (
                      <p role="alert" className="mt-2 text-sm text-red-400">{certRemoveError}</p>
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className={labelClass}>Notes</label>
            <textarea className={inputClass} rows={2} placeholder="Optional notes..." value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>

        <div className="p-5 border-t border-fieldiq-border flex-shrink-0">
          <button
            onClick={handleSubmit}
            disabled={!form.name || (!selectedTypeOption && !newTypeName.trim()) || isSubmitting || isSavingType || counterReasonMissing}
            className="w-full bg-fieldiq-accent text-fieldiq-dark py-3 rounded-xl font-semibold text-sm hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {(isSubmitting || isSavingType) && <Loader2 size={16} className="animate-spin" />}
            {item ? 'Save Changes' : 'Add Equipment'}
          </button>
        </div>
      </div>
    </div>
  );
}
